<template>
  <div class="medical-dashboard container-fluid py-3">
    <!-- Dedicated Doctor Dashboard View -->
    <DoctorDashboard v-if="isDoctor"></DoctorDashboard>

    <!-- Admin / Hospital Management View -->
    <div v-else class="dashboard-body">
      <UsersTable v-if="activeTab === 'add-user' || activeTab === 'users'"></UsersTable>
      <RoleTable v-else-if="activeTab === 'roles'"></RoleTable>
      <Tokens v-else-if="activeTab === 'tokens'"></Tokens>
      <Counter v-else-if="activeTab === 'counter'"></Counter>
      <Services v-else-if="activeTab === 'services' || activeTab === 'services-prefix'"></Services>

      <div v-else>
        <!-- Standard Page Header (for Admin & SuperAdmin) -->
        <div class="md-card md-header p-3 mb-3 d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3">
          <div>
            <h4 class="mb-1 font-weight-bold text-dark">{{ resolvedHospitalName }}</h4>
            <p class="text-sm text-secondary mb-0">
              Queue control and waiting-hall screens · {{ todayLabel }}
            </p>
          </div>

          <div class="d-flex flex-wrap align-items-center gap-2">
            <button
              type="button"
              class="btn btn-outline-teal btn-sm font-weight-bold mb-0 text-nowrap d-flex align-items-center gap-1"
              @click="loadDoctorSession(false)"
              :disabled="isFetchingSession"
              title="Refresh dashboard"
            >
              <i class="fas fa-sync-alt" :class="{ 'fa-spin': isFetchingSession }"></i>
              <span>Refresh</span>
            </button>

            <button class="btn btn-teal btn-sm text-white font-weight-bold mb-0 text-nowrap" @click="navigateToAddDoctor">
              <i class="fas fa-plus me-1"></i> Add doctor
            </button>

            <button
              type="button"
              class="btn btn-sm mb-0 text-nowrap font-weight-bold d-flex align-items-center gap-2 emergency-btn"
              :class="emergencyTokens.length ? 'btn-danger text-white' : 'btn-outline-danger'"
              @click="scrollToEmergency"
              title="Go to emergency broadcast"
            >
              <i class="fas fa-ambulance"></i>
              <span>Emergency</span>
              <span class="badge bg-white text-danger">{{ emergencyTokens.length }}</span>
            </button>
          </div>
        </div>

        <!-- Active emergency broadcast banner -->
        <div v-if="activeEmergency" class="alert alert-danger shadow-sm border-0 d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3 text-white p-3 rounded-4 animate-pulse">
          <div class="d-flex align-items-center gap-3">
            <i class="fas fa-exclamation-triangle fa-2x text-white"></i>
            <div>
              <h6 class="mb-0 font-weight-bold text-white">Emergency broadcast is live: {{ activeEmergency.title }}</h6>
              <small class="text-white-75">{{ activeEmergency.description }}</small>
            </div>
          </div>
          <button class="btn btn-light btn-sm text-danger font-weight-bold mb-0" @click="dismissEmergency">
            Stop broadcast
          </button>
        </div>

        <!-- ==================== ADMIN DASHBOARD ==================== -->
          <!-- 100% Full Width KPI Cards Row across Top -->
          <div class="row g-3 mb-3">
            <div v-for="kpi in kpiCards" :key="kpi.key" class="col-12 col-sm-6 col-xl-3">
              <div class="md-card kpi-card h-100 text-center p-3">
                <div class="kpi-icon mx-auto mb-2" :class="kpi.tone">
                  <i :class="kpi.icon"></i>
                </div>
                <p class="kpi-label mb-1">{{ kpi.label }}</p>
                <h3 class="kpi-value mb-1">
                  {{ kpi.value }}<span v-if="kpi.total !== undefined" class="kpi-total"> / {{ kpi.total }}</span>
                </h3>
                <small class="kpi-sub" :class="kpi.subClass">{{ kpi.sub }}</small>
              </div>
            </div>
          </div>

          <!-- DEPARTMENTS SECTION (100% Full Width) -->
          <div class="card border-0 shadow-sm rounded-4 mb-4 bg-white p-4">
            <div class="d-flex align-items-center justify-content-between mb-3">
              <h5 class="mb-0 font-weight-bold text-dark">Departments</h5>
              <button
                v-if="departmentSummaryList.length > 0"
                type="button"
                class="btn btn-outline-teal btn-sm mb-0 rounded-3 px-3 py-1 font-weight-bold"
                @click="showAllDepartments = !showAllDepartments"
              >
                {{ showAllDepartments ? 'Show Less' : `View all ${departmentSummaryList.length}` }}
              </button>
            </div>

            <div class="row g-3">
              <div
                v-for="dept in visibleDepartmentsList"
                :key="dept.name"
                class="col-12 col-md-4"
              >
                <div
                  class="department-card h-100 cursor-pointer"
                  :class="{ 'department-card-active': selectedDepartmentName === dept.name }"
                  @click="toggleDepartment(dept.name)"
                >
                  <div class="department-accent" :class="'accent-' + dept.accent"></div>
                  <div class="ps-2">
                    <h6 class="mb-1 font-weight-bold text-dark text-truncate">{{ dept.name }}</h6>
                    <div class="text-xs text-muted mb-2 text-truncate">{{ dept.dutySubtitle }}</div>

                    <div class="dept-progress-track mb-2">
                      <div
                        class="dept-progress-bar"
                        :class="'bar-' + dept.accent"
                        :style="{ width: Math.min(100, Math.max(8, dept.percent)) + '%' }"
                      ></div>
                    </div>

                    <div class="text-xs">
                      <strong :class="dept.isNoDoctor ? 'text-danger' : 'text-dark'">
                        {{ dept.waitingCount }} waiting
                      </strong>
                      <span :class="dept.isNoDoctor ? 'text-danger' : 'text-muted'" class="ms-1">· {{ dept.waitTime }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="visibleDepartmentsList.length === 0" class="col-12 text-center py-4 text-muted">
                <i class="fas fa-clinic-medical fa-2x mb-2 text-secondary opacity-4"></i>
                <p class="mb-0 text-sm">No departments registered yet.</p>
              </div>
            </div>
          </div>

          <!-- Two-column grid below (8 / 4) -->
          <div class="row g-3">

            <!-- ============ LEFT COLUMN ============ -->
            <div class="col-12 col-xl-8">

              <!-- Doctors in Department Table (shown ONLY when a department is clicked, styled matching mockup) -->
              <div v-if="selectedDepartmentName" class="card border-0 shadow-sm rounded-4 mb-4 bg-white p-4">
                <div class="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mb-4">
                  <div class="d-flex align-items-center gap-2">
                    <h5 class="mb-0 font-weight-bold text-dark">Doctors</h5>
                    <span class="badge bg-teal-soft text-teal text-xs font-weight-bold px-2 py-1">
                      {{ selectedDepartmentName }}
                    </span>
                  </div>

                  <div class="d-flex align-items-center gap-2">
                    <!-- Status Filter Pills matching screenshot -->
                    <div class="doctor-filter-pills d-flex align-items-center">
                      <button
                        v-for="opt in adminStatusFilterOptions"
                        :key="opt.key"
                        type="button"
                        class="btn-doctor-pill"
                        :class="{ active: adminDoctorStatusFilter === opt.key }"
                        @click="adminDoctorStatusFilter = opt.key"
                      >
                        {{ opt.label }}
                      </button>
                    </div>

                    <!-- Close button -->
                    <button
                      type="button"
                      class="btn btn-outline-secondary btn-sm mb-0 rounded-circle d-flex align-items-center justify-content-center p-0"
                      @click="selectedDepartmentName = null"
                      title="Close"
                      style="width: 28px; height: 28px;"
                    >
                      <i class="fas fa-times text-xs"></i>
                    </button>
                  </div>
                </div>

                <div class="table-responsive">
                  <table class="table md-table align-items-center mb-0">
                    <thead>
                      <tr>
                        <th class="text-secondary text-xs font-weight-bold ps-2 border-top-0">Doctor</th>
                        <th class="text-secondary text-xs font-weight-bold border-top-0">Department</th>
                        <th class="text-secondary text-xs font-weight-bold border-top-0">Counter</th>
                        <th class="text-secondary text-xs font-weight-bold border-top-0">Token Number</th>
                        <th class="text-secondary text-xs font-weight-bold border-top-0">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="item in filteredDepartmentDoctors"
                        :key="item.id"
                        @click="selectDoctorForTokenFlow(item)"
                        class="cursor-pointer doctor-row-hover"
                        :class="{ 'table-active-row': String(tokenFlowDoctorId) === String(item.id || item.user_id) }"
                        title="Click to view hourly token flow for this doctor"
                      >
                        <td class="ps-2 py-3 border-bottom-subtle">
                          <span class="font-weight-bold text-dark text-sm">{{ item.doctor_name || item.name }}</span>
                        </td>
                        <td class="py-3 border-bottom-subtle">
                          <span class="text-sm text-secondary">{{ item.speciality || item.department || selectedDepartmentName }}</span>
                        </td>
                        <td class="py-3 border-bottom-subtle">
                          <span
                            v-if="!getDoctorCounterName(item) || getDoctorCounterName(item) === '--'"
                            class="badge-no-room"
                          >
                            No counter
                          </span>
                          <span v-else class="text-sm text-dark font-weight-500">
                            {{ getDoctorCounterName(item) }}
                          </span>
                        </td>
                        <td class="py-3 border-bottom-subtle">
                          <span
                            v-if="getDoctorToken(item) && getDoctorToken(item) !== '--'"
                            class="token-chip font-weight-bold"
                          >
                            {{ getDoctorToken(item) }}
                          </span>
                          <span v-else class="text-muted text-sm font-weight-bold">--</span>
                        </td>
                        <td class="py-3 border-bottom-subtle">
                          <span class="badge-status-pill" :class="getAdminDoctorStatusBadgeClass(item.status)">
                            {{ item.status || 'Available' }}
                          </span>
                        </td>
                      </tr>
                      <tr v-if="filteredDepartmentDoctors.length === 0">
                        <td colspan="5" class="text-center py-4 text-muted">
                          <i class="fas fa-user-md fa-2x mb-2 text-secondary opacity-4"></i>
                          <p class="text-sm mb-0">No doctors found for {{ selectedDepartmentName }}<span v-if="adminDoctorStatusFilter !== 'all'"> with status "{{ adminDoctorStatusFilter }}"</span>.</p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Interactive counter drilldown -->
              <div v-if="!isDoctor || isAdminOrSuperAdmin" class="md-card p-3 mb-3">
                <div class="d-flex align-items-center justify-content-between mb-3">
                  <div>
                    <h6 class="mb-0 font-weight-bold text-dark">
                      <i class="fas fa-door-open text-teal me-2"></i>Counter Activity Drilldown
                    </h6>
                    <small class="text-muted text-xs">
                      {{ drilldownSelectedCounter ? `Drilldown for ${drilldownSelectedCounter.name}` : 'Click any counter below or on the chart to inspect today’s tokens' }}
                    </small>
                  </div>
                  <div class="d-flex align-items-center gap-2">
                    <button
                      v-if="drilldownSelectedCounter"
                      class="btn btn-outline-teal btn-sm mb-0 px-2 py-1 font-weight-bold"
                      @click="resetDrilldown"
                    >
                      <i class="fas fa-arrow-left me-1"></i>All counters
                    </button>
                    <button
                      class="btn btn-link text-secondary p-0 mb-0"
                      @click="isDrilldownCollapsed = !isDrilldownCollapsed"
                      :title="isDrilldownCollapsed ? 'Expand' : 'Collapse'"
                    >
                      <i :class="isDrilldownCollapsed ? 'fas fa-chevron-down' : 'fas fa-chevron-up'"></i>
                    </button>
                  </div>
                </div>

                <div v-show="!isDrilldownCollapsed">
                  <!-- Level 1: overview -->
                  <div v-if="!drilldownSelectedCounter" class="row g-4 align-items-center">
                    <div class="col-12 col-md-6 d-flex flex-column align-items-center justify-content-center">
                      <div class="donut-chart-container position-relative mb-2">
                        <svg class="donut-svg" viewBox="0 0 100 100">
                          <circle cx="50" cy="50" r="38" fill="transparent" stroke="#f1f5f9" stroke-width="14"></circle>
                          <circle
                            v-for="(slice, i) in drilldownCounterSlices"
                            :key="i"
                            class="donut-segment cursor-pointer"
                            cx="50"
                            cy="50"
                            r="38"
                            fill="transparent"
                            :stroke="slice.color"
                            stroke-width="14"
                            :stroke-dasharray="`${slice.dashLength} ${100 - slice.dashLength}`"
                            :stroke-dashoffset="slice.dashOffset"
                            @click="slice.counter ? selectDrilldown(slice.counter) : null"
                          ></circle>
                        </svg>
                        <div class="donut-center-label text-center">
                          <small class="text-secondary text-xs d-block">Total</small>
                          <h3 class="font-weight-bolder mb-0 text-dark">{{ counters.length }}</h3>
                          <small class="text-secondary text-xs">counters</small>
                        </div>
                      </div>
                    </div>

                    <div class="col-12 col-md-6 mt-4 mt-md-0">
                      <div class="d-flex flex-column gap-1 ps-md-3">
                        <div
                          v-for="item in drilldownLegendItems"
                          :key="item.counter.id"
                          class="d-flex align-items-center justify-content-between p-2 rounded-3 hover-bg-light cursor-pointer"
                          @click="selectDrilldown(item.counter)"
                        >
                          <div class="d-flex align-items-center gap-2">
                            <span class="legend-color-dot" :style="{ backgroundColor: item.color }"></span>
                            <span class="text-sm font-weight-bold text-dark">{{ item.counter.name }}</span>
                            <span class="text-xs text-secondary">{{ item.count }} tokens</span>
                          </div>
                          <i class="fas fa-chevron-right text-xxs text-muted"></i>
                        </div>
                        <div v-if="counters.length === 0" class="text-secondary text-sm text-center py-3">
                          No counters found
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Level 2: selected counter -->
                  <div v-else class="row g-4 align-items-start">
                    <div class="col-12 col-lg-4 d-flex flex-column align-items-center justify-content-center">
                      <div class="donut-chart-container position-relative mb-3">
                        <svg class="donut-svg" viewBox="0 0 100 100">
                          <circle cx="50" cy="50" r="38" fill="transparent" stroke="#f1f5f9" stroke-width="14"></circle>
                          <circle
                            v-for="(slice, i) in drilldownStatusSlices"
                            :key="i"
                            class="donut-segment"
                            cx="50"
                            cy="50"
                            r="38"
                            fill="transparent"
                            :stroke="slice.color"
                            stroke-width="14"
                            :stroke-dasharray="`${slice.dashLength} ${100 - slice.dashLength}`"
                            :stroke-dashoffset="slice.dashOffset"
                          ></circle>
                        </svg>
                        <div class="donut-center-label text-center">
                          <small class="text-secondary text-xs d-block">{{ drilldownSelectedCounter.name }}</small>
                          <h4 class="font-weight-bolder mb-0 text-dark">{{ drilldownCounterTokens.length }}</h4>
                          <small class="text-secondary text-xs">tokens</small>
                        </div>
                      </div>

                      <div class="d-flex flex-wrap justify-content-center gap-3">
                        <div v-for="legend in drilldownStatusLegend" :key="legend.key" class="d-flex align-items-center gap-1">
                          <span class="legend-color-dot" :class="legend.dot"></span>
                          <span class="text-xs font-weight-bold text-secondary">{{ legend.label }}:</span>
                          <span class="text-xs font-weight-bolder text-dark">{{ drilldownStatusCounts[legend.key] }}</span>
                        </div>
                      </div>
                    </div>

                    <div class="col-12 col-lg-8 border-start-lg ps-lg-4">
                      <div class="d-flex align-items-center justify-content-between mb-2">
                        <h6 class="text-xs font-weight-bolder text-uppercase text-secondary mb-0">Tokens Attended Today</h6>
                        <span class="badge bg-teal-soft text-teal text-xxs font-weight-bold">
                          {{ drilldownCounterTokens.length }} tokens
                        </span>
                      </div>

                      <div class="table-responsive" style="max-height: 250px; overflow-y: auto;">
                        <table class="table md-table align-items-center mb-0">
                          <thead>
                            <tr>
                              <th>Token</th>
                              <th>Patient</th>
                              <th>Service</th>
                              <th class="text-center">Time</th>
                              <th class="text-center">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="t in drilldownCounterTokens" :key="t.id">
                              <td class="py-2">
                                <span class="font-weight-bold text-dark text-xs">{{ t.token_number }}</span>
                              </td>
                              <td class="py-2">
                                <p class="text-xs font-weight-bold text-dark mb-0">{{ t.token_name || 'Walk-in' }}</p>
                              </td>
                              <td class="py-2">
                                <span class="text-xs text-secondary">{{ t.service?.name || t.service_name || '-' }}</span>
                              </td>
                              <td class="py-2 text-center">
                                <span class="text-xxs text-secondary">{{ formatTime(t.createdAt || t.created_at) }}</span>
                              </td>
                              <td class="py-2 text-center">
                                <span
                                  class="badge text-xxs font-weight-bold"
                                  :class="{
                                    'bg-success-soft text-success': t.status === 'completed',
                                    'bg-warning-soft text-warning': ['pending', 'waiting', 'calling', 'serving'].includes(t.status),
                                    'bg-secondary-soft text-secondary': t.status === 'skipped',
                                    'bg-danger-soft text-danger': t.status === 'rejected'
                                  }"
                                >
                                  {{ t.status }}
                                </span>
                              </td>
                            </tr>
                            <tr v-if="drilldownCounterTokens.length === 0">
                              <td colspan="5" class="text-center py-4 text-muted">
                                <i class="fas fa-inbox fa-2x mb-2 text-secondary opacity-4"></i>
                                <p class="text-sm mb-0">No tokens for this counter today.</p>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Display screens (fills the left column space and balances the layout) -->
              <div class="md-card p-3 mb-3">
                <div class="d-flex align-items-center justify-content-between mb-3">
                  <div>
                    <h6 class="mb-0 font-weight-bold text-dark">
                      <i class="fas fa-desktop text-teal me-2"></i>Display screens
                    </h6>
                    <small class="text-muted text-xs">Waiting halls and lobbies</small>
                  </div>
                  <span class="badge bg-teal-soft text-teal">{{ onlineDisplaysCount }} / {{ displays.length }} online</span>
                </div>

                <div class="row g-2">
                  <div v-for="screen in displays" :key="screen.id" class="col-12 col-md-6 col-lg-4">
                    <div class="screen-item p-2 border rounded-3 h-100 d-flex align-items-center gap-2">
                      <div class="screen-status-icon" :class="screen.online ? 'bg-success-soft text-success' : 'bg-danger-soft text-danger'">
                        <i class="fas fa-tv"></i>
                      </div>
                      <div class="flex-grow-1 min-w-0">
                        <h6 class="mb-0 text-sm font-weight-bold text-dark text-truncate">{{ screen.name }}</h6>
                        <small class="text-muted text-xs d-block text-truncate">{{ screen.location }} · {{ screen.playlist }}</small>
                      </div>
                      <span :class="screen.online ? 'badge bg-success-soft text-success' : 'badge bg-danger-soft text-danger'" class="text-xs">
                        {{ screen.online ? 'Online' : 'Offline' }}
                      </span>
                    </div>
                  </div>
                  <div v-if="displays.length === 0" class="col-12 text-center py-4 text-muted">
                    <i class="fas fa-tv fa-2x mb-2 text-secondary opacity-5"></i>
                    <p class="text-sm mb-0">No display devices found.</p>
                  </div>
                </div>
              </div>

            </div>

            <!-- ============ RIGHT COLUMN ============ -->
            <div class="col-12 col-xl-4">

              <!-- Emergency broadcast -->
              <div ref="emergencyPanel" class="md-card p-3 mb-3">
                <div class="mb-3 d-flex align-items-center justify-content-between">
                  <div>
                    <h6 class="mb-0 font-weight-bold text-dark">
                      <i class="fas fa-bullhorn text-danger me-2"></i>Emergency broadcast
                    </h6>
                    <small class="text-muted text-xs">Alert all waiting-hall screens instantly</small>
                  </div>
                  <span v-if="emergencyTokens.length" class="badge bg-danger-soft text-danger animate-pulse">
                    {{ emergencyTokens.length }} Active Alert(s)
                  </span>
                </div>

                <div v-if="emergencyTokens.length" class="emergency-list d-flex flex-column gap-2 mb-3">
                  <button
                    v-for="em in emergencyTokens"
                    :key="em.id"
                    type="button"
                    class="btn btn-outline-danger d-flex align-items-center justify-content-between p-2 rounded-3 mb-0 text-start w-100 emergency-btn"
                    @click="broadcastEmergency(em)"
                  >
                    <div class="text-start">
                      <span class="font-weight-bold text-sm text-danger">{{ em.token_number }}</span>
                      <small class="text-muted text-xs d-block">{{ em.token_name || 'Emergency patient' }}</small>
                    </div>
                    <i class="fas fa-ambulance text-danger"></i>
                  </button>
                </div>
                <div v-else class="text-center py-3 text-muted">
                  <small>No active emergency tokens.</small>
                </div>
              </div>

              <!-- Token flow today Card (Matching Mockup with Dept, Doctor, Date & Hourly Filters) -->
              <div class="md-card p-3 mb-3 token-flow-card">
                <!-- Header with Title, Timing Option, and Date Picker -->
                <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                  <h6 class="mb-0 font-weight-bold text-dark text-nowrap">
                    {{ isTokenFlowToday ? 'Token flow today' : `Token flow (${formattedTokenFlowDate})` }}
                  </h6>
                  <div class="d-flex align-items-center gap-1">
                    <!-- Timing Option Dropdown (Right next to date picker as requested) -->
                    <select
                      v-model="tokenFlowHour"
                      class="form-select form-select-sm token-flow-timing-select"
                      title="Filter by timing / hour"
                    >
                      <option value="all">All Hours</option>
                      <option
                        v-for="slot in tokenFlowHourOptions"
                        :key="slot.hour"
                        :value="slot.hour"
                      >
                        {{ slot.timeRange }}
                      </option>
                    </select>

                    <input
                      type="date"
                      v-model="tokenFlowDate"
                      @change="onTokenFlowDateChange"
                      class="form-control form-control-sm token-flow-date-picker"
                      title="Select date"
                    />
                  </div>
                </div>

                <!-- Department and Doctor Filters Row -->
                <div class="row g-2 mb-3">
                  <div class="col-6">
                    <label class="form-label text-xxs text-secondary text-uppercase font-weight-bold mb-1">
                      Department
                    </label>
                    <select
                      v-model="tokenFlowDepartment"
                      @change="onTokenFlowDeptChange"
                      class="form-select form-select-sm token-flow-select"
                    >
                      <option value="all">All Departments</option>
                      <option
                        v-for="dept in departmentSummaryList"
                        :key="dept.name"
                        :value="dept.name"
                      >
                        {{ dept.name }}
                      </option>
                    </select>
                  </div>
                  <div class="col-6">
                    <label class="form-label text-xxs text-secondary text-uppercase font-weight-bold mb-1">
                      Doctor
                    </label>
                    <select
                      v-model="tokenFlowDoctorId"
                      @change="tokenFlowHour = 'all'"
                      class="form-select form-select-sm token-flow-select"
                      :disabled="isFetchingHistoricalTokens"
                    >
                      <option value="all">All Doctors</option>
                      <option
                        v-for="doc in tokenFlowDoctorOptions"
                        :key="doc.id"
                        :value="doc.id"
                      >
                        {{ doc.name }}
                      </option>
                    </select>
                  </div>
                </div>

                <!-- Loading spinner for historical fetch -->
                <div v-if="isFetchingHistoricalTokens" class="text-center py-4">
                  <div class="spinner-border spinner-border-sm text-teal" role="status"></div>
                  <small class="text-muted text-xs ms-2">Loading tokens for {{ formattedTokenFlowDate }}...</small>
                </div>

                <template v-else>
                  <!-- Active Hour Indicator Banner (appears only when an hour bar is clicked) -->
                  <div v-if="tokenFlowHour !== 'all'" class="d-flex align-items-center justify-content-between px-2 py-1 mb-2 rounded-2 text-xs" style="background-color: #f0f7ff; border: 1px solid #d0e3ff;">
                    <span class="text-primary font-weight-bold d-flex align-items-center gap-1">
                      <i class="far fa-clock"></i>
                      <span>{{ selectedHourTimeRange }}</span>
                      <small class="text-secondary fw-normal">({{ selectedHourTokens.length }} token{{ selectedHourTokens.length === 1 ? '' : 's' }})</small>
                    </span>
                    <button
                      type="button"
                      class="btn btn-link text-primary text-xxs p-0 mb-0 font-weight-bold text-decoration-none"
                      @click="tokenFlowHour = 'all'"
                      title="Clear hourly filter and show full day"
                    >
                      Show Full Day <i class="fas fa-times ms-1"></i>
                    </button>
                  </div>

                  <!-- 4 Status Summary Pills matching screenshot -->
                  <div class="token-flow-pills-row d-flex align-items-center gap-2 mb-3">
                    <div class="token-flow-pill flex-fill" :class="{ 'token-flow-pill-active': tokenFlowHour !== 'all' }">
                      <div class="token-flow-pill-val">{{ tokenFlowStats.issued }}</div>
                      <div class="token-flow-pill-lbl">Issued</div>
                    </div>
                    <div class="token-flow-pill flex-fill">
                      <div class="token-flow-pill-val text-success">{{ tokenFlowStats.served }}</div>
                      <div class="token-flow-pill-lbl">Served</div>
                    </div>
                    <div class="token-flow-pill flex-fill">
                      <div class="token-flow-pill-val text-secondary">{{ tokenFlowStats.skipped }}</div>
                      <div class="token-flow-pill-lbl">Skipped</div>
                    </div>
                    <div class="token-flow-pill flex-fill">
                      <div class="token-flow-pill-val text-warning">{{ tokenFlowStats.waiting }}</div>
                      <div class="token-flow-pill-lbl">Waiting</div>
                    </div>
                  </div>

                  <!-- Hourly Bar Chart matching screenshot (Click bar to select hour) -->
                  <div class="token-flow-chart-wrap mb-2">
                    <div class="token-flow-bars d-flex align-items-end justify-content-between">
                      <div
                        v-for="bar in tokenFlowHourlyData"
                        :key="bar.hour"
                        class="token-flow-bar-col d-flex flex-column align-items-center cursor-pointer"
                        :class="{ 'bar-col-selected': String(tokenFlowHour) === String(bar.hour) }"
                        :title="`Click to filter: ${bar.timeRange} (${bar.count} token${bar.count === 1 ? '' : 's'})`"
                        @click="toggleTokenFlowHour(bar.hour)"
                      >
                        <!-- Count above bar -->
                        <span
                          class="token-flow-bar-val mb-1"
                          :class="{ 'font-weight-bold text-dark': bar.isPeak, 'text-primary font-weight-bolder': String(tokenFlowHour) === String(bar.hour) }"
                        >
                          {{ bar.count }}
                        </span>

                        <!-- Bar track and fill -->
                        <div class="token-flow-bar-track">
                          <div
                            class="token-flow-bar"
                            :class="{ 'bar-peak': bar.isPeak, 'bar-active': String(tokenFlowHour) === String(bar.hour) }"
                            :style="{ height: `${bar.heightPct}%` }"
                          ></div>
                        </div>

                        <!-- Hour label below bar -->
                        <span
                          class="token-flow-bar-hour mt-1"
                          :class="{ 'font-weight-bold text-dark': bar.isPeak, 'text-primary font-weight-bolder': String(tokenFlowHour) === String(bar.hour) }"
                        >
                          {{ bar.label }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- Busiest time indicator & Patient toggle -->
                  <div class="token-flow-footer pt-1 d-flex align-items-center justify-content-between flex-wrap gap-1">
                    <span class="text-secondary text-xs font-weight-500">
                      Busiest time:
                      <strong class="text-dark ms-1">{{ tokenFlowBusiestTime }}</strong>
                    </span>
                    <button
                      type="button"
                      class="btn btn-link text-teal text-xs p-0 mb-0 font-weight-bold text-decoration-none"
                      @click="showHourlyDetails = !showHourlyDetails"
                    >
                      <i :class="showHourlyDetails ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="me-1"></i>
                      {{ showHourlyDetails ? 'Hide Token Details' : `Show Token Details (${displayedTokenFlowTokens.length})` }}
                    </button>
                  </div>

                  <!-- Collapsible Hourly / Date Patient Token List (With Counter Name) -->
                  <div v-if="showHourlyDetails" class="hourly-patient-drawer mt-2 pt-2 border-top">
                    <div class="d-flex align-items-center justify-content-between mb-2">
                      <small class="text-xxs text-uppercase text-secondary font-weight-bold">
                        Tokens ({{ tokenFlowHour === 'all' ? (isTokenFlowToday ? 'Today' : formattedTokenFlowDate) : selectedHourTimeRange }})
                      </small>
                      <span class="badge bg-teal-soft text-teal text-xxs font-weight-bold">
                        {{ displayedTokenFlowTokens.length }} total
                      </span>
                    </div>

                    <div style="max-height: 250px; overflow-y: auto;" class="pe-1">
                      <div
                        v-for="tok in displayedTokenFlowTokens"
                        :key="tok.id"
                        class="p-2 mb-2 rounded bg-light border-start-3"
                        :style="{ borderLeft: `3px solid ${getHourlyTokenBorderColor(tok.status)}` }"
                      >
                        <div class="d-flex align-items-center justify-content-between mb-1">
                          <div class="d-flex align-items-center gap-1">
                            <span class="font-weight-bold text-dark text-xs badge bg-white border text-dark">
                              {{ tok.token_number }}
                            </span>
                            <span class="text-xs text-dark font-weight-bold text-truncate" style="max-width: 130px;">
                              {{ tok.token_name || 'Walk-in' }}
                            </span>
                          </div>
                          <span class="badge text-xxs font-weight-bold" :class="getTokenStatusBadgeClass(tok.status)">
                            {{ tok.status || 'pending' }}
                          </span>
                        </div>

                        <!-- Counter and Service Information Row -->
                        <div class="d-flex align-items-center justify-content-between flex-wrap gap-1 text-xxs mt-1 pt-1 border-top border-light">
                          <div class="d-flex align-items-center gap-1">
                            <span class="badge bg-white text-secondary border font-weight-bold">
                              <i class="fas fa-desktop text-teal me-1"></i>{{ getCounterNameForToken(tok) }}
                            </span>
                            <span class="text-secondary text-truncate" style="max-width: 110px;">
                              {{ tok.service?.name || tok.department || 'General OPD' }}
                            </span>
                          </div>

                          <span class="text-muted font-weight-bold">
                            <i class="far fa-clock me-1"></i>{{ formatTime(tok.createdAt || tok.created_at || tok.date || tok.token_time) }}
                          </span>
                        </div>
                      </div>

                      <div v-if="displayedTokenFlowTokens.length === 0" class="text-center py-3 text-muted text-xs">
                        <i class="far fa-calendar-times text-secondary opacity-5 me-1"></i>
                        No tokens found for {{ tokenFlowHour === 'all' ? (formattedTokenFlowDate || 'this date') : selectedHourTimeRange }}
                      </div>
                    </div>
                  </div>
                </template>
              </div>

              <!-- Booked and walk-in Card (Total average per day) -->
              <div class="md-card p-3 mb-3 booked-walkin-card">
                <div class="d-flex align-items-center justify-content-between mb-3">
                  <div>
                    <h6 class="mb-0 font-weight-bold text-dark">Booked and walk-in</h6>
                    <small class="text-muted text-xs">Total average per day</small>
                  </div>
                  <span class="badge bg-light text-secondary text-xs px-2 py-1 font-weight-bold">
                    {{ bookingTypesTotalCount }} total today
                  </span>
                </div>

                <!-- Multi-segment Progress Bar -->
                <div class="booked-walkin-progress mb-3">
                  <div
                    v-for="opt in bookingTypeOptionsList"
                    :key="opt.key"
                    :style="{ width: opt.pct + '%', backgroundColor: opt.color }"
                    :title="`${opt.label}: ${opt.count} (${opt.pct}%)`"
                    class="booking-type-progress-seg"
                  ></div>
                </div>

                <!-- Exactly the 6 requested options -->
                <div class="booked-walkin-rows d-flex flex-column">
                  <div
                    v-for="(opt, idx) in bookingTypeOptionsList"
                    :key="opt.key"
                    class="booked-walkin-row d-flex align-items-center justify-content-between py-2"
                    :class="{ 'border-bottom': idx < bookingTypeOptionsList.length - 1 }"
                  >
                    <div class="d-flex align-items-center gap-2">
                      <span class="booking-type-dot" :style="{ backgroundColor: opt.color }"></span>
                      <span class="text-sm text-secondary font-weight-500">{{ opt.label }}</span>
                    </div>
                    <div class="d-flex align-items-center gap-2">
                      <span class="text-xs text-muted" v-if="opt.count > 0">({{ opt.count }})</span>
                      <span class="text-sm font-weight-bold text-dark">{{ opt.pct }}%</span>
                    </div>
                  </div>
                </div>
              </div>

            </div>

          </div>

      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { io } from "socket.io-client";
import DoctorDashboard from "@/views/DoctorDashboard.vue";
import UsersTable from "./components/UsersTable.vue";
import RoleTable from "./components/RoleTable.vue";
import Tokens from "./components/Tokens.vue";
import Counter from "./components/Counter.vue";
import Services from "./components/Services.vue";

/* ------------------------------------------------------------------ */
/* Shared helpers (previously copy-pasted across the component)        */
/* ------------------------------------------------------------------ */
const SERVING_STATUSES = ["serving", "processing"];
const QUEUE_NOW_STATUSES = ["serving", "processing", "calling"];
const QUEUE_NEXT_STATUSES = ["pending", "waiting"];
const DOCTOR_KEYWORDS = ["doctor", "physician", "consultant", "specialist", "surgeon"];
const NON_DOCTOR_ROLE_KEYWORDS = ["tokenuser", "tokenmanager", "tokenmanger", "operator", "teller", "superadmin", "supervisor", "tenant", "reception", "nurse"];

const lower = (v) => String(v || "").toLowerCase();
const stripDr = (v) => lower(v).replace(/^dr\.?\s*/i, "").trim();
const cleanRoleKey = (v) => lower(v).replace(/[^a-z0-9]/gi, "");
const hasAny = (text, words) => words.some((w) => text.includes(w));

const getAuthToken = () =>
  localStorage.getItem("token") || localStorage.getItem("auth_token") || localStorage.getItem("admin_token");
const getApiBase = () => (process.env.VUE_APP_API_BASE_URL || "/api").replace(/\/+$/, "");
const getStoredName = () => localStorage.getItem("userName") || localStorage.getItem("name") || "";
const getStoredEmail = () => localStorage.getItem("adminEmail") || localStorage.getItem("email") || "";

const readStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}") || {};
  } catch (e) {
    return {};
  }
};
const readAllShifts = () => {
  try {
    return JSON.parse(localStorage.getItem("hospitalDoctorShifts") || "{}") || {};
  } catch (e) {
    return {};
  }
};
const writeLocalStatus = (status) => {
  localStorage.setItem("availability_status", status);
  localStorage.setItem("doctor_status", status);
};

const getInitials = (name) =>
  String(name || "")
    .replace(/^dr\.?\s*/i, "")
    .trim()
    .split(" ")
    .map((n) => n[0])
    .join("")
    .substring(0, 2)
    .toUpperCase() || "DR";

const formatDoctorName = (rawName) => {
  const raw = String(rawName || "");
  return raw.startsWith("Dr.") ? raw : `Dr. ${raw.replace(/^dr\.?\s*/i, "").trim()}`;
};

const shiftLabel = (s) =>
  s && s.start_time && s.end_time ? `${s.start_time} - ${s.end_time}` : (s && s.shift) || "09:00 - 17:00";

const isEmergencyToken = (t) =>
  !!t &&
  lower(t.status) !== "completed" &&
  (t.is_emergency === true || t.is_emergency === 1 || t.is_emergency === "true" || lower(t.is_emergency) === "yes");

const roleNameOfUser = (u) =>
  lower(u?.role_name || u?.role_slug || (u?.role && (u.role.name || u.role.slug)) || u?.role || "");

const detectIsDoctor = () => {
  const r = cleanRoleKey(localStorage.getItem("userRole") || localStorage.getItem("role") || "");
  if (r.includes("doctor") || r === "physician") return true;
  const uRole = roleNameOfUser(readStoredUser());
  return uRole.includes("doctor") || uRole.includes("physician");
};

/** Pull an array out of the many response shapes this API returns. */
const extractItems = (resVal) => {
  if (!resVal) return [];
  const val = resVal.value !== undefined ? resVal.value : resVal;
  const body = val && val.data !== undefined ? val.data : val;
  if (Array.isArray(body)) return body;
  if (!body || typeof body !== "object") return [];
  const keys = ["data", "result", "tokens", "rows", "users", "candidates", "attendants", "roles", "counters", "branches", "services"];
  for (const k of keys) if (Array.isArray(body[k])) return body[k];
  if (body.data && typeof body.data === "object") {
    for (const k of keys) if (Array.isArray(body.data[k])) return body.data[k];
  }
  return [];
};

/** Find the token currently being served for a user / counter / attendant name. */
const findServingToken = (tokens, userId, counterId, cleanName) => {
  const cn = lower(cleanName);
  return tokens.find((tok) => {
    const tu = String(tok.user_id || "");
    const tc = String(tok.counter_id || "");
    const ta = stripDr(tok.token_attended);
    return (
      (userId && tu === String(userId)) ||
      (counterId && tc === String(counterId)) ||
      (cn && ta && ta !== "no" && (ta.includes(cn) || cn.includes(ta)))
    );
  });
};

export default {
  name: "MedicalDashboard",
  components: {
    DoctorDashboard,
    UsersTable,
    RoleTable,
    Tokens,
    Counter,
    Services,
  },
  beforeMount() {
    this.$store.state.showSidenav = true;
    this.$store.state.showNavbar = true;
    this.$store.state.showFooter = true;

    const isAuth = localStorage.getItem("medical_authenticated");
    const role = lower(localStorage.getItem("userRole"));

    if (isAuth !== "true" && !getAuthToken() && role !== "doctor") {
      this.$router.push("/sign-in");
    }
  },
  mounted() {
    try {
      const user = readStoredUser();
      const realComp = user.company_name || user.tenant_name || user.hospital_name || user.companyShortName || "";
      if (realComp) {
        this.tenantName = realComp;
        localStorage.setItem("tenant_name", realComp);
        localStorage.setItem("company_name", realComp);
      } else {
        const storedComp = localStorage.getItem("company_name") || localStorage.getItem("tenant_name") || "";
        this.tenantName =
          storedComp && !lower(storedComp).includes("metrotrust")
            ? storedComp
            : user.name && !lower(user.name).includes("admin") && !lower(user.name).includes("dr.")
              ? user.name
              : "Star Hospital";
      }

      if (this.isDoctor && user.role && typeof user.role === "object") {
        let rolePerms = user.role.permissions || user.role.modules || [];
        if (typeof rolePerms === "string") {
          try { rolePerms = JSON.parse(rolePerms); } catch (e) { rolePerms = [rolePerms]; }
        }
        if (Array.isArray(rolePerms) && rolePerms.length > 0) {
          const cleanPerms = rolePerms.map((p) => (typeof p === "object" && p !== null ? p.code || p.name || p.module_name || p.id : p));
          localStorage.setItem("allowedModules", JSON.stringify(cleanPerms));
          localStorage.setItem("userModules", JSON.stringify(rolePerms));
        }
      }
    } catch (e) {
      console.warn("Could not parse tenant user in mounted:", e);
    }

    // loadDoctorSession already fetches displays and tokens
    if (typeof window !== "undefined" && window.speechSynthesis && window.speechSynthesis.cancel) {
      window.speechSynthesis.cancel();
    }
    this.loadDoctorSession();

    try {
      const rawBase = process.env.VUE_APP_API_BASE_URL || window.location.origin;
      const parsedUrl = new URL(rawBase.startsWith("http") ? rawBase : window.location.origin);
      const socketUrl = process.env.VUE_APP_SOCKET_URL || `${parsedUrl.protocol}//${parsedUrl.host}`;

      this.socket = io(socketUrl, {
        auth: { token: getAuthToken() },
        transports: ["websocket", "polling"],
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 2000,
      });

      const handleLiveStatusSync = (payload) => {
        if (payload && (payload.status || payload.availability_status)) {
          // The window listener below applies the change (and refreshes tokens);
          // other components listening for this event get it too.
          window.dispatchEvent(new CustomEvent("doctor-status-synced", { detail: payload }));
        } else {
          this.fetchEmergencyTokens();
        }
      };

      const handleLiveShiftSync = (payload) => {
        if (payload && payload.is_shift_update) this.loadDoctorSession(true);
        else this.fetchEmergencyTokens();
      };

      this.socket.on("doctor_status_updated", handleLiveStatusSync);
      this.socket.on("doctor_availability_changed", handleLiveStatusSync);
      this.socket.on("counter_status_updated", handleLiveStatusSync);
      this.socket.on("doctor_shift_updated", handleLiveShiftSync);
      this.socket.on("token_updated", () => this.fetchEmergencyTokens());
      this.socket.on("token_created", () => this.fetchEmergencyTokens());
    } catch (sockInitErr) {
      console.warn("Could not initialize socket in MedicalDashboard:", sockInitErr);
    }

    this.timerInterval = setInterval(() => {
      this.currentTime = new Date();
    }, 1000);

    window.addEventListener("storage", this.handleStorageEvent);
    window.addEventListener("doctor-availability-changed", this.handleDoctorAvailabilityEvent);
    window.addEventListener("doctor-status-synced", this.handleDoctorStatusSynced);

    this.onTokenSyncHandler = () => {
      this.loadDoctorSession(true);
      this.fetchEmergencyTokens();
    };
    window.addEventListener("token-created", this.onTokenSyncHandler);
    window.addEventListener("tokens-updated", this.onTokenSyncHandler);
  },
  beforeUnmount() {
    if (this.timerInterval) clearInterval(this.timerInterval);
    if (this.socket) this.socket.disconnect();

    if (this.onTokenSyncHandler) {
      window.removeEventListener("token-created", this.onTokenSyncHandler);
      window.removeEventListener("tokens-updated", this.onTokenSyncHandler);
    }

    window.removeEventListener("storage", this.handleStorageEvent);
    window.removeEventListener("doctor-availability-changed", this.handleDoctorAvailabilityEvent);
    window.removeEventListener("doctor-status-synced", this.handleDoctorStatusSynced);
  },
  data() {
    const u = readStoredUser();
    const resolvedTenantName =
      u.company_name || u.tenant_name || u.hospital_name || u.companyShortName ||
      localStorage.getItem("company_name") || localStorage.getItem("tenant_name") || localStorage.getItem("companyName") || "";

    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const localTodayIso = `${y}-${m}-${day}`;

    return {
      tenantName: resolvedTenantName,
      activeTab: "dashboard",
      doctorFilter: "",
      activeEmergency: null,
      activeDoctorSession: null,
      currentTime: new Date(),
      timerInterval: null,
      socket: null,
      isFetchingSession: false,
      tokensRequest: null,
      cachedRoles: null,
      cachedBranches: null,
      doctors: [],
      displays: [],
      emergencyTokens: [],
      counters: [],
      branches: [],
      services: [],
      todayTokens: [],

      // Interactive counter drilldown
      drilldownSelectedCounter: null,
      isDrilldownCollapsed: false,
      drilldownColorPalette: ["#6ee7b7", "#9ca3af", "#f59e0b", "#3b82f6", "#ec4899", "#8b5cf6", "#14b8a6", "#f97316"],
      drilldownStatusLegend: [
        { key: "completed", label: "Completed", dot: "bg-success" },
        { key: "pending", label: "Pending", dot: "bg-warning" },
        { key: "skipped", label: "Skipped", dot: "bg-secondary" },
        { key: "rejected", label: "Rejected", dot: "bg-danger" },
      ],

      // Admin Dashboard state
      selectedDepartmentName: null,
      showAllDepartments: false,
      showAllDevices: false,
      adminDoctorStatusFilter: "all",
      adminStatusFilterOptions: [
        { key: "all", label: "All" },
        { key: "available", label: "Available" },
        { key: "busy", label: "Busy" },
        { key: "break", label: "Break" },
        { key: "offline", label: "Offline" },
      ],
      adminActiveTab: "overview",

      // Token flow analytics state
      tokenFlowDepartment: "all",
      tokenFlowDoctorId: "all",
      tokenFlowHour: "all",
      showHourlyDetails: false,
      tokenFlowDate: localTodayIso,
      historicalTokensCache: {},
      isFetchingHistoricalTokens: false,
    };
  },
  computed: {
    todayLabel() {
      return this.currentTime.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    },
    visibleTokens() {
      return this.todayTokens.filter((t) => !this.isOtherTenant(t));
    },
    totalTokensCount() {
      return this.visibleTokens.length;
    },
    activeTokensCount() {
      return this.visibleTokens.filter((t) => !["completed", "rejected"].includes(lower(t.status))).length;
    },
    completedTokensCount() {
      return this.visibleTokens.filter((t) => lower(t.status) === "completed").length;
    },
    skippedTokensCount() {
      return this.visibleTokens.filter(
        (t) =>
          lower(t.status) === "skipped" ||
          t.is_skipped === true ||
          t.is_skipped === 1 ||
          String(t.is_skipped || "").toLowerCase() === "true" ||
          (Array.isArray(this.localSkippedTokenIds) && this.localSkippedTokenIds.includes(String(t.id)))
      ).length;
    },
    totalCountersCount() {
      return this.counters.length;
    },
    activeCountersCount() {
      return this.counters.filter((c) => lower(c.status) === "active" || c.status === 1).length;
    },
    onlineDisplaysCount() {
      return this.displays.filter((d) => d.online).length;
    },
    kpiCards() {
      const online = this.onlineDisplaysCount;
      const totalScreens = this.displays.length;
      const waiting = this.waitingTokensCount;
      return [
        {
          key: "tokens",
          label: "Total Patients",
          value: this.totalTokensCount,
          icon: "fas fa-users fa-lg",
          tone: "tone-teal",
          sub: `${this.activeTokensCount} active · ${this.completedTokensCount} done`,
          subClass: "text-success",
        },
        {
          key: "counters",
          label: "OPD counters",
          value: this.totalCountersCount,
          icon: "fas fa-door-open fa-lg",
          tone: "tone-blue",
          sub: `${this.activeCountersCount} rooms active`,
          subClass: "text-success",
        },
        {
          key: "displays",
          label: "Display screens",
          value: online,
          total: totalScreens,
          icon: "fas fa-tv fa-lg",
          tone: "tone-amber",
          sub: totalScreens > 0 ? `${online} connected` : "No screens online",
          subClass: online > 0 ? "text-success" : "text-danger",
        },
        {
          key: "waiting",
          label: "Waiting Patients",
          value: waiting,
          icon: "fas fa-user-clock fa-lg",
          tone: "tone-pink",
          sub: waiting > 0 ? `${waiting} ${waiting === 1 ? 'patient' : 'patients'} waiting` : "No patients waiting",
          subClass: waiting > 0 ? "text-pink" : "text-muted",
        },
      ];
    },
    currentTenantId() {
      const u = readStoredUser();
      const raw =
        u.tenant_id ??
        u.tenantId ??
        u.tenant?.id ??
        u.Tenant?.id ??
        localStorage.getItem("tenant_id") ??
        localStorage.getItem("tenantId") ??
        null;
      if (raw === null || raw === undefined) return null;
      const s = String(raw).trim();
      if (!s || s === "null" || s === "undefined" || s === "0" || s === "NaN") return null;
      return s;
    },
    bookingTypesTotalCount() {
      return (this.visibleTokens || []).length;
    },
    bookingTypeOptionsList() {
      const tokens = this.visibleTokens || [];
      const total = tokens.length;

      const counts = {
        "Walk-in": 0,
        "Appointment": 0,
        "Online": 0,
        "Emergency": 0,
        "VIP": 0,
        "Teleconsultation": 0,
      };

      tokens.forEach((t) => {
        const bt = String(t.booking_type || t.type || "").trim().toLowerCase();
        if (bt.includes("appointment")) {
          counts["Appointment"]++;
        } else if (bt.includes("online")) {
          counts["Online"]++;
        } else if (bt.includes("teleconsult")) {
          counts["Teleconsultation"]++;
        } else if (bt.includes("vip")) {
          counts["VIP"]++;
        } else if (bt.includes("emerg") || t.is_emergency) {
          counts["Emergency"]++;
        } else {
          counts["Walk-in"]++;
        }
      });

      const colors = {
        "Walk-in": "#0d9488",
        "Appointment": "#2563eb",
        "Online": "#8b5cf6",
        "Emergency": "#ef4444",
        "VIP": "#f59e0b",
        "Teleconsultation": "#06b6d4",
      };

      const keys = ["Walk-in", "Appointment", "Online", "Emergency", "VIP", "Teleconsultation"];

      if (total > 0) {
        return keys.map((key) => {
          const count = counts[key] || 0;
          const pct = Math.round((count / total) * 100);
          return {
            key,
            label: key,
            count,
            pct,
            color: colors[key],
          };
        });
      }

      // Default baseline when demo / empty
      const demoPcts = {
        "Walk-in": 39,
        "Appointment": 35,
        "Online": 14,
        "Emergency": 4,
        "VIP": 4,
        "Teleconsultation": 4,
      };

      return keys.map((key) => ({
        key,
        label: key,
        count: 0,
        pct: demoPcts[key],
        color: colors[key],
      }));
    },
    adminKpiCards() {
      const onlineScreens = this.adminOnlineDevicesCount;
      const totalScreens = this.adminDisplayDevices.length;
      const activeDocs = this.activeDoctorsCount;
      const totalDocs = this.allAdminDoctors.length;
      const totalPatients = this.totalTokensCount;
      const activePatients = this.activeTokensCount;
      const completedPatients = this.completedTokensCount;

      return [
        {
          key: "patients",
          label: "Total Patients",
          value: totalPatients,
          icon: "fas fa-users fa-lg",
          tone: "tone-teal",
          sub: `${activePatients} active · ${completedPatients} done`,
          subClass: "text-teal",
        },
        {
          key: "departments",
          label: "Total Departments",
          value: this.departmentSummaryList.length,
          icon: "fas fa-clinic-medical fa-lg",
          tone: "tone-blue",
          sub: `${this.activeDepartmentsCount} active on duty`,
          subClass: "text-success",
        },
        {
          key: "devices",
          label: "Display screens",
          value: onlineScreens,
          total: totalScreens,
          icon: "fas fa-tv fa-lg",
          tone: "tone-amber",
          sub: totalScreens > 0 ? `${onlineScreens} of ${totalScreens} online` : "No screens online",
          subClass: onlineScreens > 0 && onlineScreens === totalScreens ? "text-success" : "text-warning",
        },
        {
          key: "doctors",
          label: "Doctors on duty",
          value: activeDocs,
          total: totalDocs,
          icon: "fas fa-user-md fa-lg",
          tone: "tone-teal",
          sub: `${activeDocs} of ${totalDocs} on duty now`,
          subClass: "text-success",
        },
      ];
    },
    departmentSummaryList() {
      const deptMap = new Map();

      const getOrCreateDept = (rawName) => {
        const name = String(rawName || "").trim();
        if (!name || name === "--" || lower(name) === "undefined" || lower(name) === "null") return null;
        const key = name.toLowerCase();
        if (!deptMap.has(key)) {
          deptMap.set(key, {
            name,
            doctors: new Set(),
            onDutyDoctors: new Set(),
            waitingTokens: 0,
            servingTokens: 0,
            completedTokens: 0,
            shifts: [],
          });
        }
        return deptMap.get(key);
      };

      const findExistingDept = (rawName) => {
        if (!rawName) return null;
        const key = String(rawName).trim().toLowerCase();
        if (!key) return null;
        if (deptMap.has(key)) return deptMap.get(key);
        for (const [k, dept] of deptMap.entries()) {
          if (k === key || k.includes(key) || key.includes(k)) {
            return dept;
          }
        }
        return null;
      };

      // 1. Strictly register ONLY this tenant admin's actual Departments (from Branches / Departments module)
      const tenantBranches = (this.branches || []).filter((b) => !this.isOtherTenant(b));
      tenantBranches.forEach((b) => {
        const name = (b.branch_name || b.department_name || b.name || b.title || "").trim();
        if (name && name !== "--") {
          getOrCreateDept(name);
        }
      });

      // 2. Fallback: if no branches registered yet, check tenant's doctors for genuine department names (excluding counters & rooms)
      if (deptMap.size === 0) {
        this.allAdminDoctors.forEach((doc) => {
          const dept = (doc.department || doc.speciality || "").trim();
          if (dept && dept !== "--" && !lower(dept).includes("counter") && !lower(dept).includes("room")) {
            getOrCreateDept(dept);
          }
        });
      }

      if (deptMap.size === 0) {
        return [];
      }

      // 3. Group tenant's doctors into the registered departments
      this.allAdminDoctors.forEach((doc) => {
        let bucket = null;
        for (const dept of deptMap.values()) {
          if (this.isDoctorInDepartment(doc.raw || doc, dept.name)) {
            bucket = dept;
            break;
          }
        }
        if (!bucket) return;
        const docKey = String(doc.id || doc.name);
        bucket.doctors.add(docKey);
        const st = String(doc.status || "").toLowerCase();
        if (st.includes("available") || st.includes("busy") || st.includes("break")) {
          bucket.onDutyDoctors.add(docKey);
        }
        const shiftStr = doc.raw?.shift || doc.shift;
        if (shiftStr && shiftStr !== "--") {
          bucket.shifts.push(shiftStr);
        }
      });

      // 4. Match tenant's today tokens to the registered departments (never create new departments from tokens or counters)
      const counterBranchMap = new Map();
      (this.counters || []).forEach((c) => {
        if (this.isOtherTenant(c)) return;
        const bName = c.branch?.branch_name || c.branch_name || c.speciality || c.department || "";
        if (bName) counterBranchMap.set(String(c.id), bName);
        if (c.name && bName) counterBranchMap.set(lower(c.name), bName);
      });

      (this.todayTokens || []).forEach((t) => {
        if (this.isOtherTenant(t)) return;

        let targetDeptName = t.department || t.speciality || t.branch_name || t.service?.branch_name || "";
        if (!targetDeptName && t.counter_id && counterBranchMap.has(String(t.counter_id))) {
          targetDeptName = counterBranchMap.get(String(t.counter_id));
        }
        if (!targetDeptName && t.counter_name && counterBranchMap.has(lower(t.counter_name))) {
          targetDeptName = counterBranchMap.get(lower(t.counter_name));
        }

        let bucket = findExistingDept(targetDeptName);
        if (!bucket && deptMap.size === 1) {
          bucket = Array.from(deptMap.values())[0];
        }
        if (!bucket) return;

        const st = String(t.status || "").toLowerCase();
        if (QUEUE_NEXT_STATUSES.includes(st)) {
          bucket.waitingTokens++;
        } else if (QUEUE_NOW_STATUSES.includes(st)) {
          bucket.servingTokens++;
        } else if (st === "completed" || st === "done") {
          bucket.completedTokens++;
        }
      });

      const palette = ["teal", "amber", "teal", "red", "amber", "teal"];
      let idx = 0;

      return Array.from(deptMap.values()).map((dept) => {
        const totalDocs = dept.doctors.size;
        const onDuty = dept.onDutyDoctors.size;
        const pct = totalDocs > 0 ? Math.round((onDuty / totalDocs) * 100) : 0;
        const waiting = dept.waitingTokens;

        let waitTime = "";
        let isNoDoctor = false;

        if (totalDocs > 0 && onDuty === 0 && waiting > 0) {
          isNoDoctor = true;
          waitTime = "no doctor";
        } else if (waiting === 0) {
          waitTime = "wait 0 min";
        } else {
          const waitMins = Math.max(3, waiting * 5);
          waitTime = `wait ${waitMins} min`;
        }

        let accent = "teal";
        if (isNoDoctor) {
          accent = "red";
        } else if (pct < 100 || onDuty === 0) {
          accent = "amber";
        } else {
          accent = palette[idx % palette.length];
        }
        idx++;

        let dutySubtitle = `${onDuty} of ${totalDocs} doctors on duty`;
        if (dept.shifts.length > 0 && onDuty === 1 && totalDocs === 1) {
          const shiftEnd = dept.shifts[0].split("-")[1]?.trim() || dept.shifts[0];
          dutySubtitle = `1 of 1 · ends at ${shiftEnd}`;
        }

        return {
          name: dept.name,
          doctorsOnDuty: onDuty,
          totalDoctors: totalDocs,
          percent: pct,
          dutySubtitle,
          waitingCount: waiting,
          waitTime,
          isNoDoctor,
          accent,
        };
      });
    },
    visibleDepartmentsList() {
      if (this.showAllDepartments) {
        return this.departmentSummaryList;
      }
      return this.departmentSummaryList.slice(0, 6);
    },
    activeDepartmentsCount() {
      return this.departmentSummaryList.filter((d) => d.doctorsOnDuty > 0).length;
    },
    activeDepartmentName() {
      return this.selectedDepartmentName || "";
    },
    departmentDoctors() {
      if (!this.selectedDepartmentName || !Array.isArray(this.doctors)) return [];
      const tenantDoctors = this.doctors.filter((d) => this.isDoctorUser(d) && !this.isOtherTenant(d));
      return tenantDoctors.filter((doc) => this.isDoctorInDepartment(doc, this.selectedDepartmentName));
    },
    filteredDepartmentDoctors() {
      const list = this.departmentDoctors;
      const filter = String(this.adminDoctorStatusFilter || "all").toLowerCase();
      let res = list;
      if (filter !== "all") {
        res = res.filter((doc) => {
          const s = String(doc.status || "").toLowerCase();
          if (filter === "available") return s.includes("available");
          if (filter === "busy") return s.includes("busy");
          if (filter === "break") return s.includes("break");
          if (filter === "offline") return s.includes("offline");
          return true;
        });
      }
      const q = (this.doctorFilter || "").toLowerCase().trim();
      if (!q) return res;
      return res.filter((doc) => {
        const name = String(doc.doctor_name || doc.name || "").toLowerCase();
        const spec = String(doc.speciality || doc.department || "").toLowerCase();
        const room = String(doc.room || doc.counter_name || "").toLowerCase();
        return name.includes(q) || spec.includes(q) || room.includes(q);
      });
    },
    allAdminDoctors() {
      if (!Array.isArray(this.doctors)) return [];

      const tenantDoctors = this.doctors.filter((d) => this.isDoctorUser(d) && !this.isOtherTenant(d));

      return tenantDoctors.map((doc, idx) => {
        const rawName = doc.doctor_name || doc.name || `Doctor ${idx + 1}`;
        const name = formatDoctorName(rawName);
        const dept = doc.speciality || doc.department || (doc.branch?.branch_name ? doc.branch.branch_name : "General OPD");
        const room = doc.room || doc.counter_name || (doc.counter ? doc.counter.name : "");
        const hasNoRoom = !room || room === "No room" || room === "--";
        const status = doc.availability_status || doc.status || "Available";

        return {
          id: doc.id || doc.user_id || `tenant-doc-${idx}`,
          name,
          department: dept,
          room: hasNoRoom ? "No room" : room,
          hasNoRoom,
          status,
          queue: this.getDoctorQueueCount(doc),
          utilization: this.getDoctorUtilization(doc),
          raw: doc,
        };
      });
    },
    filteredAdminDoctors() {
      const filter = String(this.adminDoctorStatusFilter || "all").toLowerCase();
      if (filter === "all") return this.allAdminDoctors;
      return this.allAdminDoctors.filter((doc) => {
        const s = String(doc.status || "").toLowerCase();
        if (filter === "available") return s.includes("available");
        if (filter === "busy") return s.includes("busy");
        if (filter === "break") return s.includes("break");
        if (filter === "offline") return s.includes("offline");
        return true;
      });
    },
    activeDoctorsCount() {
      return this.allAdminDoctors.filter((d) => {
        const s = String(d.status || "").toLowerCase();
        return s.includes("available") || s.includes("busy");
      }).length;
    },
    adminDisplayDevices() {
      if (!Array.isArray(this.displays)) return [];
      return this.displays
        .filter((d) => !this.isOtherTenant(d))
        .map((d, idx) => {
          const isOnline = Boolean(d.online || lower(d.status) === "online");
          const hasRoom = Boolean(d.location && d.location !== "Unassigned" && d.location !== "--");
          let statusText = isOnline ? "Online" : "Offline";
          if (!hasRoom && isOnline) {
            statusText = "Unassigned";
          }
          const isProblem = !isOnline || statusText === "Unassigned";

          return {
            id: d.id || idx + 1,
            name: d.name || `Display TV #${d.id || idx + 1}`,
            location: !isOnline
              ? (d.location ? `${d.location} · Offline` : "Offline")
              : (d.location || "Not assigned to a room"),
            status: statusText,
            online: isOnline,
            isProblem,
          };
        });
    },
    adminProblemDevices() {
      return this.adminDisplayDevices.filter((d) => d.isProblem);
    },
    visibleAdminDevices() {
      if (this.showAllDevices) {
        return this.adminDisplayDevices;
      }
      return this.adminProblemDevices;
    },
    adminOnlineDevicesCount() {
      return this.adminDisplayDevices.filter((d) => d.online).length;
    },
    deviceOnlinePercent() {
      const total = this.adminDisplayDevices.length;
      if (!total) return 0;
      return Math.round((this.adminOnlineDevicesCount / total) * 100);
    },
    waitingTokensList() {
      return this.visibleTokens.filter((t) => QUEUE_NEXT_STATUSES.includes(lower(t.status)));
    },
    waitingTokensCount() {
      return this.waitingTokensList.length;
    },
    hasEmergencyTokens() {
      return this.visibleTokens.some((t) =>
        (t.is_emergency === true || t.is_emergency === 1 || t.is_emergency === "true" || String(t.is_emergency || "").toLowerCase() === "yes") &&
        ["pending", "waiting"].includes(String(t.status || "").toLowerCase())
      );
    },
    emergencyPendingTokens() {
      return this.visibleTokens.filter((t) =>
        (t.is_emergency === true || t.is_emergency === 1 || t.is_emergency === "true" || String(t.is_emergency || "").toLowerCase() === "yes") &&
        ["pending", "waiting"].includes(String(t.status || "").toLowerCase())
      );
    },
    resolvedHospitalName() {
      const user = readStoredUser();
      if (user.company_name) return user.company_name;
      if (user.tenant_name) return user.tenant_name;
      if (user.hospital_name) return user.hospital_name;
      if (user.companyShortName) return user.companyShortName;
      if (user.name && !lower(user.name).includes("admin") && !lower(user.name).includes("dr.")) return user.name;

      const c = localStorage.getItem("company_name") || localStorage.getItem("tenant_name");
      if (c && !lower(c).includes("metrotrust")) return c;
      return this.tenantName && !lower(this.tenantName).includes("metrotrust") ? this.tenantName : "Star Hospital";
    },
    currentUserRole() {
      return cleanRoleKey(localStorage.getItem("userRole") || localStorage.getItem("role") || "");
    },
    isDoctor() {
      return detectIsDoctor();
    },
    isAdminOrSuperAdmin() {
      if (this.isDoctor) return false;
      const r = this.currentUserRole;
      const userType = Number(readStoredUser().type || 0);
      return (
        userType === 1 ||
        hasAny(r, ["superadmin", "admin", "tenant", "manager", "receptionist", "reception", "nurse", "tokenmanager", "tokenmanger"]) ||
        r === "1"
      );
    },
    filteredDoctors() {
      const list = this.doctors.filter((d) => this.isDoctorUser(d));
      if (this.doctorFilter) {
        const q = lower(this.doctorFilter);
        return list.filter(
          (d) => lower(d.name).includes(q) || lower(d.speciality).includes(q) || (d.room && lower(d.room).includes(q))
        );
      }
      return list;
    },
    drilldownCounterTokenMap() {
      const map = new Map();
      this.counters.forEach((c) => map.set(c.id, []));

      this.todayTokens.forEach((tok) => {
        const cId = tok.counter_id ? Number(tok.counter_id) : null;
        if (cId && map.has(cId)) {
          map.get(cId).push(tok);
        } else {
          const tokAtt = lower(tok.token_attended).trim();
          const matched = this.counters.find((c) => {
            const att = lower(c.user?.name || c.attendant_name).trim();
            return att && tokAtt && tokAtt !== "no" && (att.includes(tokAtt) || tokAtt.includes(att));
          });
          if (matched && map.has(matched.id)) map.get(matched.id).push(tok);
        }
      });
      return map;
    },
    drilldownLegendItems() {
      return this.counters.map((c, idx) => ({
        counter: c,
        count: (this.drilldownCounterTokenMap.get(c.id) || []).length,
        color: this.drilldownColorPalette[idx % this.drilldownColorPalette.length],
      }));
    },
    drilldownCounterSlices() {
      const items = this.drilldownLegendItems;
      const totalTokens = items.reduce((sum, item) => sum + item.count, 0);

      if (totalTokens === 0) {
        const slicePct = 100 / Math.max(1, items.length);
        let offset = 25;
        return items.map((item) => {
          const res = {
            counter: item.counter,
            count: 0,
            color: "#e2e8f0",
            dashLength: Math.max(0.5, slicePct - 1),
            dashOffset: -offset,
          };
          offset += slicePct;
          return res;
        });
      }

      let accumulatedPct = 0;
      return items.map((item) => {
        const pct = (item.count / totalTokens) * 100;
        const slice = {
          counter: item.counter,
          count: item.count,
          color: item.color,
          dashLength: pct > 0 ? Math.max(0.5, pct - 0.5) : 0,
          dashOffset: -(accumulatedPct - 25),
        };
        accumulatedPct += pct;
        return slice;
      });
    },
    drilldownCounterTokens() {
      if (!this.drilldownSelectedCounter) return [];
      return this.drilldownCounterTokenMap.get(this.drilldownSelectedCounter.id) || [];
    },
    drilldownStatusCounts() {
      const toks = this.drilldownCounterTokens;
      const count = (list) => toks.filter((t) => list.includes(lower(t.status))).length;
      return {
        completed: count(["completed"]),
        pending: count(["pending", "waiting", "calling", "serving", "processing"]),
        skipped: count(["skipped"]),
        rejected: count(["rejected"]),
      };
    },
    drilldownStatusSlices() {
      const sc = this.drilldownStatusCounts;
      const total = sc.completed + sc.pending + sc.skipped + sc.rejected;

      const slicesConfig = [
        { name: "Completed", count: sc.completed, color: "#22c55e" },
        { name: "Pending", count: sc.pending, color: "#f59e0b" },
        { name: "Skipped", count: sc.skipped, color: "#94a3b8" },
        { name: "Rejected", count: sc.rejected, color: "#ef4444" },
      ];

      if (total === 0) {
        return [{ name: "Empty", count: 0, color: "#cbd5e1", dashLength: 100, dashOffset: 25 }];
      }

      let accumulatedPct = 0;
      return slicesConfig.map((scItem) => {
        const pct = (scItem.count / total) * 100;
        const res = {
          ...scItem,
          dashLength: pct > 0 ? Math.max(0.5, pct - 0.5) : 0,
          dashOffset: -(accumulatedPct - 25),
        };
        accumulatedPct += pct;
        return res;
      });
    },
    todayDateIso() {
      const d = new Date();
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    isTokenFlowToday() {
      return !this.tokenFlowDate || this.tokenFlowDate === this.todayDateIso;
    },
    formattedTokenFlowDate() {
      if (!this.tokenFlowDate) return "";
      try {
        const parts = this.tokenFlowDate.split("-");
        if (parts.length === 3) {
          const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
          return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
        }
      } catch (e) {
        // fallback
      }
      return this.tokenFlowDate;
    },
    tokenFlowDoctorOptions() {
      if (!Array.isArray(this.allAdminDoctors)) return [];
      if (!this.tokenFlowDepartment || this.tokenFlowDepartment === "all") {
        return this.allAdminDoctors;
      }
      return this.allAdminDoctors.filter((doc) => {
        return this.isDoctorInDepartment(doc.raw || doc, this.tokenFlowDepartment);
      });
    },
    displayedTokenFlowTokens() {
      if (this.tokenFlowHour !== "all" && this.tokenFlowHour !== null && this.tokenFlowHour !== undefined) {
        return this.selectedHourTokens;
      }
      return this.filteredTokenFlowTokens;
    },
    tokenFlowSourceTokens() {
      const dStr = this.tokenFlowDate || this.todayDateIso;
      if (this.historicalTokensCache[dStr]) {
        return (this.historicalTokensCache[dStr] || []).filter((t) => !this.isOtherTenant(t));
      }
      if (this.isTokenFlowToday) {
        return (this.todayTokens || []).filter((t) => !this.isOtherTenant(t));
      }
      return [];
    },
    filteredTokenFlowTokens() {
      let list = this.tokenFlowSourceTokens;

      // Filter by department
      if (this.tokenFlowDepartment && this.tokenFlowDepartment !== "all") {
        const targetDept = String(this.tokenFlowDepartment).trim().toLowerCase();
        const deptDocIds = new Set(
          this.tokenFlowDoctorOptions.map((d) => String(d.id || d.user_id || ""))
        );
        const deptDocNames = this.tokenFlowDoctorOptions.map((d) => stripDr(d.name).toLowerCase()).filter(Boolean);

        list = list.filter((t) => {
          const tDept = String(t.department || t.speciality || t.branch_name || t.service?.branch_name || "").trim().toLowerCase();
          if (tDept && (tDept === targetDept || tDept.includes(targetDept) || targetDept.includes(tDept))) return true;

          const uId = String(t.user_id || "");
          const dId = String(t.doctor_id || "");
          if (uId && deptDocIds.has(uId)) return true;
          if (dId && deptDocIds.has(dId)) return true;

          const tAtt = stripDr(t.token_attended || "").toLowerCase();
          if (tAtt && tAtt !== "no" && deptDocNames.some((dn) => tAtt.includes(dn) || dn.includes(tAtt))) return true;

          const cId = String(t.counter_id || "");
          if (cId && Array.isArray(this.counters)) {
            const c = this.counters.find((cnt) => String(cnt.id) === cId);
            if (c) {
              const cDept = String(c.branch?.branch_name || c.branch_name || c.speciality || c.department || "").trim().toLowerCase();
              if (cDept && (cDept === targetDept || cDept.includes(targetDept) || targetDept.includes(cDept))) return true;
            }
          }
          return false;
        });
      }

      // Filter by doctor
      if (this.tokenFlowDoctorId && this.tokenFlowDoctorId !== "all") {
        const docId = String(this.tokenFlowDoctorId);
        const selectedDoc = this.allAdminDoctors.find((d) => String(d.id) === docId);
        const docCleanName = selectedDoc ? stripDr(selectedDoc.name).toLowerCase() : "";
        const docCounter = selectedDoc?.room ? lower(selectedDoc.room).replace(/^room\s*/i, "").trim() : "";

        list = list.filter((t) => {
          if (String(t.user_id) === docId || String(t.doctor_id) === docId) return true;
          const tAtt = stripDr(t.token_attended || "").toLowerCase();
          if (docCleanName && tAtt && tAtt !== "no" && (tAtt.includes(docCleanName) || docCleanName.includes(tAtt))) return true;
          if (docCounter) {
            const tRoom = lower(t.counter?.name || t.counter_name || t.room || "").replace(/^room\s*/i, "").trim();
            if (tRoom && tRoom === docCounter) return true;
          }
          return false;
        });
      }

      return list;
    },
    tokenFlowHourOptions() {
      return [
        { hour: 8, label: "8", timeRange: "8 to 9 AM" },
        { hour: 9, label: "9", timeRange: "9 to 10 AM" },
        { hour: 10, label: "10", timeRange: "10 to 11 AM" },
        { hour: 11, label: "11", timeRange: "11 AM to 12 PM" },
        { hour: 12, label: "12", timeRange: "12 to 1 PM" },
        { hour: 13, label: "1", timeRange: "1 to 2 PM" },
        { hour: 14, label: "2", timeRange: "2 to 3 PM" },
        { hour: 15, label: "3", timeRange: "3 to 4 PM" },
        { hour: 16, label: "4", timeRange: "4 to 5 PM" },
        { hour: 17, label: "5", timeRange: "5 to 6 PM" },
      ];
    },
    selectedHourTimeRange() {
      if (this.tokenFlowHour === "all") return "All Hours";
      const found = this.tokenFlowHourOptions.find((s) => s.hour === Number(this.tokenFlowHour));
      return found ? found.timeRange : `${this.tokenFlowHour}:00`;
    },
    selectedHourTokens() {
      if (this.tokenFlowHour === "all") return [];
      const targetHour = Number(this.tokenFlowHour);
      return this.filteredTokenFlowTokens.filter((t) => {
        const h = this.getTokenHour(t);
        return h !== null && h === targetHour;
      });
    },
    tokenFlowStats() {
      let list = this.filteredTokenFlowTokens;

      if (this.tokenFlowHour !== "all" && this.tokenFlowHour !== null && this.tokenFlowHour !== undefined) {
        const targetHour = Number(this.tokenFlowHour);
        list = list.filter((t) => {
          const h = this.getTokenHour(t);
          return h !== null && h === targetHour;
        });
      }

      const issued = list.length;
      let served = 0;
      let skipped = 0;
      let waiting = 0;

      list.forEach((t) => {
        const st = String(t.status || "").toLowerCase();
        if (st === "completed" || st === "done") {
          served++;
        } else if (
          st === "skipped" ||
          t.is_skipped === true ||
          t.is_skipped === 1 ||
          String(t.is_skipped || "").toLowerCase() === "true" ||
          (Array.isArray(this.localSkippedTokenIds) && this.localSkippedTokenIds.includes(String(t.id)))
        ) {
          skipped++;
        } else if (["pending", "waiting", "calling", "serving", "processing"].includes(st)) {
          waiting++;
        }
      });

      return { issued, served, skipped, waiting };
    },
    tokenFlowHourlyData() {
      const slots = this.tokenFlowHourOptions;
      const list = this.filteredTokenFlowTokens;
      const hourCounts = {};
      slots.forEach((s) => { hourCounts[s.hour] = 0; });

      list.forEach((t) => {
        const h = this.getTokenHour(t);
        if (h !== null && hourCounts[h] !== undefined) {
          hourCounts[h]++;
        }
      });

      const counts = slots.map((s) => hourCounts[s.hour]);
      const maxCount = Math.max(0, ...counts);

      return slots.map((slot) => {
        const count = hourCounts[slot.hour] || 0;
        let heightPct = 0;
        if (maxCount > 0 && count > 0) {
          heightPct = Math.max(12, Math.round((count / maxCount) * 100));
        } else if (count === 0) {
          heightPct = 4;
        }
        return {
          ...slot,
          count,
          heightPct,
          isPeak: maxCount > 0 && count === maxCount,
          isSelected: this.tokenFlowHour !== "all" && Number(this.tokenFlowHour) === slot.hour,
        };
      });
    },
    tokenFlowBusiestTime() {
      const data = this.tokenFlowHourlyData;
      if (!data || data.length === 0) return "--";
      let peak = data[0];
      data.forEach((item) => {
        if (item.count > peak.count) peak = item;
      });
      if (peak.count === 0) return "No tokens in hours";
      return peak.timeRange;
    },
  },
  watch: {
    selectedDepartmentName(newVal) {
      if (newVal) {
        this.tokenFlowDepartment = newVal;
      } else {
        this.tokenFlowDepartment = "all";
      }
      this.tokenFlowDoctorId = "all";
      this.tokenFlowHour = "all";
    },
    tokenFlowDepartment() {
      this.tokenFlowHour = "all";
    },
    tokenFlowDoctorId() {
      this.tokenFlowHour = "all";
    },
    tokenFlowDate() {
      this.tokenFlowHour = "all";
    },
  },
  methods: {
    isOtherTenant(rec) {
      if (!rec) return false;
      const myTenantId = this.currentTenantId;
      if (!myTenantId) return false;
      const recTenantId =
        rec.tenant_id ??
        rec.tenantId ??
        rec.TenantId ??
        rec.tenant?.id ??
        rec.Tenant?.id ??
        (rec.user && (rec.user.tenant_id ?? rec.user.tenantId)) ??
        (rec.raw && (rec.raw.tenant_id ?? rec.raw.tenantId)) ??
        null;
      if (recTenantId === null || recTenantId === undefined) return false;
      const s = String(recTenantId).trim();
      if (!s || s === "null" || s === "undefined" || s === "0") return false;
      return s !== String(myTenantId);
    },

    /* ------------------------- Admin Dashboard Helpers ------------------------- */
    getDoctorQueueCount(doc) {
      if (!doc) return 0;
      const dept = lower(doc.department || doc.speciality || "");
      const room = lower(doc.room || doc.counter_name || "");
      const matched = this.todayTokens.filter((t) => {
        if (this.isOtherTenant(t)) return false;
        const st = lower(t.status || "");
        if (!QUEUE_NEXT_STATUSES.includes(st) && !QUEUE_NOW_STATUSES.includes(st)) return false;
        const tDept = lower(t.department || t.speciality || "");
        const tCounter = lower(t.counter_name || t.counter?.name || "");
        return (dept && tDept.includes(dept)) || (room && tCounter.includes(room));
      });
      return matched.length;
    },
    getDoctorUtilization(doc) {
      if (!doc) return 0;
      const s = lower(doc.status || "");
      if (s.includes("offline")) return 0;
      const queueCount = this.getDoctorQueueCount(doc);
      if (s.includes("busy")) {
        return Math.min(100, Math.max(70, 70 + queueCount * 5));
      }
      if (s.includes("available")) {
        return queueCount > 0 ? Math.min(100, Math.max(40, 30 + queueCount * 10)) : 25;
      }
      if (s.includes("break")) return 20;
      return 0;
    },
    getAdminDoctorStatusBadgeClass(status) {
      const s = String(status || "").toLowerCase();
      if (s.includes("available")) return "badge-status-available";
      if (s.includes("busy")) return "badge-status-busy";
      if (s.includes("break")) return "badge-status-break";
      if (s.includes("offline")) return "badge-status-offline";
      return "badge-status-available";
    },
    getDoctorCounterName(item) {
      if (!item) return "--";
      if (item.counter_name && item.counter_name !== "--" && item.counter_name !== "No room") return item.counter_name;
      if (item.room && item.room !== "--" && item.room !== "No room") return item.room;
      if (item.counter && item.counter.name) return item.counter.name;
      const docId = String(item.id || item.user_id || item.raw?.id || item.raw?.user_id || "");
      if (docId && Array.isArray(this.counters)) {
        const matched = this.counters.find((c) => {
          if (!c || this.isOtherTenant(c)) return false;
          return String(c.user_id) === docId || (c.user && String(c.user.id) === docId);
        });
        if (matched && (matched.name || matched.counter_name)) return matched.name || matched.counter_name;
      }
      return "--";
    },
    getDoctorToken(item) {
      if (!item) return "--";
      if (item.currentToken && item.currentToken !== "--") return item.currentToken;
      if (item.current_token && item.current_token !== "--") return item.current_token;
      const docId = String(item.id || item.user_id || item.raw?.id || item.raw?.user_id || "");
      const cName = stripDr(item.doctor_name || item.name || "");
      const counterId = String(item.counter_id || item.raw?.counter_id || "");
      if (Array.isArray(this.todayTokens)) {
        const active = this.todayTokens.find((t) => {
          if (!t || this.isOtherTenant(t)) return false;
          const st = String(t.status || "").toLowerCase();
          if (!SERVING_STATUSES.includes(st)) return false;
          const tUid = String(t.user_id || "");
          const tCid = String(t.counter_id || "");
          const tAtt = stripDr(t.token_attended || "");
          if (docId && tUid === docId) return true;
          if (counterId && tCid === counterId) return true;
          if (cName && tAtt && tAtt !== "no" && (tAtt.includes(cName) || cName.includes(tAtt))) return true;
          return false;
        });
        if (active && active.token_number) return active.token_number;
      }
      return "--";
    },
    getDeviceBadgeClass(status) {
      const s = String(status || "").toLowerCase();
      if (s.includes("offline")) return "badge-device-offline";
      if (s.includes("unassigned")) return "badge-device-unassigned";
      return "badge-device-online";
    },

    /* ------------------------- small UI helpers ------------------------- */
    getCounterNameForToken(tok) {
      if (!tok) return "No counter";
      if (tok.counter && (tok.counter.name || tok.counter.counter_name)) {
        return tok.counter.name || tok.counter.counter_name;
      }
      if (tok.counter_name && tok.counter_name !== "--" && tok.counter_name !== "No room") {
        return tok.counter_name;
      }
      const cId = tok.counter_id ? String(tok.counter_id) : "";
      if (cId && Array.isArray(this.counters)) {
        const found = this.counters.find((c) => String(c.id) === cId);
        if (found && (found.name || found.counter_name)) {
          return found.name || found.counter_name;
        }
      }
      const att = stripDr(tok.token_attended);
      if (att && att !== "no" && Array.isArray(this.counters)) {
        const found = this.counters.find((c) => {
          const cAtt = stripDr(c.attendant_name || c.user?.name);
          return cAtt && (cAtt.includes(att) || att.includes(cAtt));
        });
        if (found && (found.name || found.counter_name)) {
          return found.name || found.counter_name;
        }
      }
      return "No counter";
    },
    tokenMatchesDate(tok, targetDateStr) {
      if (!tok || !targetDateStr) return false;
      const raw = tok.date || tok.booking_date || tok.bookingDate || tok.token_date || tok.createdAt || tok.created_at;
      if (!raw) return true;
      const s = String(raw).trim();
      const datePart = s.split("T")[0].split(" ")[0].trim();
      if (datePart === targetDateStr) return true;

      if (s.includes("-")) {
        const parts = s.split("-");
        if (parts.length === 3 && parts[2].length === 4) {
          const reordered = `${parts[2]}-${parts[1].padStart(2, "0")}-${parts[0].padStart(2, "0")}`;
          if (reordered === targetDateStr) return true;
        }
      }
      return false;
    },
    toggleDepartment(deptName) {
      if (this.selectedDepartmentName === deptName) {
        this.selectedDepartmentName = null;
        this.tokenFlowDepartment = "all";
      } else {
        this.selectedDepartmentName = deptName;
        this.tokenFlowDepartment = deptName;
      }
      this.tokenFlowDoctorId = "all";
    },
    selectDepartment(deptName) {
      this.selectedDepartmentName = deptName;
      this.tokenFlowDepartment = deptName || "all";
      this.tokenFlowDoctorId = "all";
    },
    onTokenFlowDeptChange() {
      this.tokenFlowDoctorId = "all";
      this.tokenFlowHour = "all";
      if (this.tokenFlowDepartment !== "all") {
        this.selectedDepartmentName = this.tokenFlowDepartment;
      }
    },
    async onTokenFlowDateChange() {
      this.tokenFlowHour = "all";
      this.showHourlyDetails = true; // Auto-open token details on date selection
      if (!this.tokenFlowDate) return;
      await this.fetchHistoricalTokens(this.tokenFlowDate);
    },
    getTokenHour(t) {
      if (!t) return null;
      const rawDate = t.createdAt || t.created_at || t.date || t.token_time || t.time;
      if (!rawDate) return null;
      const d = new Date(rawDate);
      if (!isNaN(d.getTime())) return d.getHours();
      if (typeof rawDate === "string" && rawDate.includes(":")) {
        const parts = rawDate.split(":");
        const h = parseInt(parts[0], 10);
        if (!isNaN(h)) return h;
      }
      return null;
    },
    getHourlyTokenCount(hour) {
      const list = this.filteredTokenFlowTokens;
      const targetHour = Number(hour);
      return list.filter((t) => {
        const h = this.getTokenHour(t);
        return h !== null && h === targetHour;
      }).length;
    },
    toggleTokenFlowHour(hour) {
      if (String(this.tokenFlowHour) === String(hour)) {
        this.tokenFlowHour = "all";
      } else {
        this.tokenFlowHour = hour;
      }
    },
    getTokenStatusBadgeClass(status) {
      const s = String(status || "").toLowerCase();
      if (s === "completed" || s === "done") return "bg-success-soft text-success";
      if (["pending", "waiting", "calling", "serving", "processing"].includes(s)) return "bg-warning-soft text-warning";
      if (s === "skipped") return "bg-secondary-soft text-secondary";
      if (s === "rejected") return "bg-danger-soft text-danger";
      return "bg-light text-secondary";
    },
    getHourlyTokenBorderColor(status) {
      const s = String(status || "").toLowerCase();
      if (s === "completed" || s === "done") return "#10b981";
      if (s === "serving" || s === "calling") return "#0284c7";
      if (s === "pending" || s === "waiting") return "#f59e0b";
      if (s === "skipped") return "#64748b";
      if (s === "rejected") return "#ef4444";
      return "#94a3b8";
    },
    formatTime(val) {
      if (!val) return "--";
      try {
        const d = new Date(val);
        if (isNaN(d.getTime())) return String(val);
        return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      } catch (e) {
        return String(val);
      }
    },
    async fetchHistoricalTokens(dateStr) {
      if (!dateStr) return;
      this.isFetchingHistoricalTokens = true;
      try {
        const token = getAuthToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const base = getApiBase();
        const apiUrl = base.endsWith("/api") ? base : `${base}/api`;

        const url = `${apiUrl}/v1/admin/tokens/list?date=${dateStr}&booking_date=${dateStr}&all=true`;
        const res = await axios.get(url, { headers });
        let items = extractItems(res);

        if (Array.isArray(items) && items.length > 0) {
          const dateMatched = items.filter((t) => this.tokenMatchesDate(t, dateStr));
          if (dateMatched.length > 0) {
            items = dateMatched;
          }
        }
        this.historicalTokensCache = { ...this.historicalTokensCache, [dateStr]: items };
      } catch (err) {
        console.warn("Could not fetch historical tokens:", dateStr, err);
        this.historicalTokensCache = { ...this.historicalTokensCache, [dateStr]: [] };
      } finally {
        this.isFetchingHistoricalTokens = false;
      }
    },
    selectDoctorForTokenFlow(doc) {
      if (!doc) return;
      const id = doc.id || doc.user_id;
      if (id) {
        this.tokenFlowDoctorId = id;
        this.tokenFlowHour = "all";
        const targetCard = document.querySelector(".token-flow-card");
        if (targetCard && targetCard.scrollIntoView) {
          targetCard.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      }
    },
    isDoctorInDepartment(doc, targetDepartmentName) {
      if (!doc || !targetDepartmentName) return false;
      const target = String(targetDepartmentName).trim().toLowerCase();
      if (!target) return false;

      // Find the branch corresponding to targetDepartmentName
      const matchedBranch = (this.branches || []).find((b) => {
        if (!b || this.isOtherTenant(b)) return false;
        const bName = String(b.branch_name || b.department_name || b.name || b.title || "").trim().toLowerCase();
        return bName === target || bName.includes(target) || target.includes(bName);
      });

      const branchId = matchedBranch ? String(matchedBranch.id) : null;
      const branchUserId = matchedBranch && matchedBranch.user_id ? String(matchedBranch.user_id) : null;
      const branchContact = matchedBranch && matchedBranch.contact_person ? String(matchedBranch.contact_person).trim().toLowerCase() : "";

      const docId = String(doc.id || doc.user_id || doc.raw?.id || doc.raw?.user_id || "").trim();
      const rawDocName = String(doc.doctor_name || doc.name || doc.raw?.name || "").trim().toLowerCase();
      const cleanDocName = rawDocName.replace(/^dr\.?\s*/i, "").trim();

      // 0. Multiple doctors assigned to department (doctor_ids / doctors)
      if (matchedBranch) {
        if (Array.isArray(matchedBranch.doctor_ids) && matchedBranch.doctor_ids.map(String).includes(docId)) return true;
        if (Array.isArray(matchedBranch.doctors) && matchedBranch.doctors.some((d) => String(d.id || d.user_id) === docId)) return true;
        if (typeof matchedBranch.doctor_ids === "string") {
          try {
            const parsed = JSON.parse(matchedBranch.doctor_ids);
            if (Array.isArray(parsed) && parsed.map(String).includes(docId)) return true;
          } catch (e) {
            if (matchedBranch.doctor_ids.split(",").map((s) => s.trim()).includes(docId)) return true;
          }
        }
      }

      // 1. Direct branch assignment: doctor assigned as department in-charge/contact in branches table
      if (branchUserId && docId && branchUserId === docId) return true;
      if (branchContact && cleanDocName && (branchContact.includes(cleanDocName) || cleanDocName.includes(branchContact))) return true;

      // 2. Doctor's branch_id or dept_id matches the department's branch id
      const docBranchId = String(doc.branch_id || doc.dept_id || doc.raw?.branch_id || doc.raw?.dept_id || doc.raw?.branch?.id || doc.branch?.id || "").trim();
      if (branchId && docBranchId && branchId === docBranchId) return true;

      // 3. Doctor's department / speciality / branch_name text matching
      const docDept = String(doc.department || doc.speciality || doc.department_name || doc.raw?.department || doc.raw?.speciality || doc.raw?.department_name || "").trim().toLowerCase();
      if (docDept && (docDept === target || docDept.includes(target) || target.includes(docDept))) return true;

      const docBranchName = String(doc.branch_name || doc.raw?.branch_name || doc.raw?.branch?.branch_name || "").trim().toLowerCase();
      if (docBranchName && (docBranchName === target || docBranchName.includes(target) || target.includes(docBranchName))) return true;

      // 4. Counter / Room association (if doctor's counter is linked to this branch)
      const docRoom = String(doc.room || doc.counter_name || doc.raw?.counter_name || "").trim().toLowerCase();
      if (docRoom) {
        const matchedCounter = (this.counters || []).find((c) => {
          if (!c || this.isOtherTenant(c)) return false;
          const cName = String(c.name || c.counter_name || "").trim().toLowerCase();
          return cName === docRoom || docRoom.includes(cName) || cName.includes(docRoom);
        });
        if (matchedCounter) {
          if (branchId && matchedCounter.branch_id && String(matchedCounter.branch_id) === branchId) return true;
          const cBranchName = String(matchedCounter.branch?.branch_name || matchedCounter.branch_name || matchedCounter.speciality || matchedCounter.department || "").trim().toLowerCase();
          if (cBranchName && (cBranchName === target || cBranchName.includes(target) || target.includes(cBranchName))) return true;
        }
      }

      // 5. Counter ID association (if doctor has counter_id linked to this branch)
      const docCounterId = String(doc.counter_id || doc.raw?.counter_id || "").trim();
      if (docCounterId) {
        const matchedCounter = (this.counters || []).find((c) => {
          if (!c || this.isOtherTenant(c)) return false;
          return String(c.id) === docCounterId;
        });
        if (matchedCounter) {
          if (branchId && matchedCounter.branch_id && String(matchedCounter.branch_id) === branchId) return true;
          const cBranchName = String(matchedCounter.branch?.branch_name || matchedCounter.branch_name || matchedCounter.speciality || matchedCounter.department || "").trim().toLowerCase();
          if (cBranchName && (cBranchName === target || cBranchName.includes(target) || target.includes(cBranchName))) return true;
        }
      }

      return false;
    },
    selectDrilldown(counter) {
      this.drilldownSelectedCounter = counter;
    },
    resetDrilldown() {
      this.drilldownSelectedCounter = null;
    },
    scrollToEmergency() {
      const el = this.$refs.emergencyPanel;
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
    },
    roomLabel(item) {
      const room = item.room || item.counter_name || item.name || "01";
      return lower(room).startsWith("room") ? room : `Room ${room}`;
    },
    getDrilldownStatusBadgeClass(status) {
      const s = lower(status);
      if (s === "completed") return "bg-success text-white";
      if (["calling", "serving", "processing"].includes(s)) return "bg-primary text-white";
      if (s === "skipped") return "bg-secondary text-white";
      if (s === "rejected") return "bg-danger text-white";
      return "bg-warning-soft text-warning border-warning";
    },
    getEmergencyRoom(em) {
      if (!em) return "";
      return em.counter?.name || em.counter_name || em.room || "";
    },
    getStatusBadgeClass(status) {
      const s = lower(status).trim();
      if (s.includes("busy") || s.includes("consultation")) return "bg-danger-soft text-danger border-danger";
      if (s.includes("break")) return "bg-warning-soft text-warning border-warning";
      if (s.includes("off")) return "bg-secondary-soft text-secondary border-secondary";
      return "bg-success-soft text-success border-success";
    },
    getStatusDotClass(status) {
      const s = lower(status).trim();
      if (s.includes("busy") || s.includes("consultation")) return "dot-busy";
      if (s.includes("break")) return "dot-break";
      if (s.includes("off")) return "dot-offline";
      return "dot-available";
    },
    isStatusMatch(item, targetStatus) {
      const s = lower(item.availability_status || item.status || "Available").trim();
      const t = lower(targetStatus).trim();
      if (t === "busy") return s.includes("busy") || s.includes("consultation");
      if (t.includes("break")) return s.includes("break");
      if (t.includes("off")) return s.includes("off");
      if (t === "available") return s === "available" || (!s.includes("busy") && !s.includes("break") && !s.includes("off"));
      return s === t;
    },
    navigateToAddDoctor() {
      this.$router.push({ name: "Users" });
    },
    triggerEmergency(title, description) {
      this.activeEmergency = { title, description };
    },
    broadcastEmergency(em) {
      const room = this.getEmergencyRoom(em);
      const attended = em.token_attended && em.token_attended !== "No" ? `Attended by ${em.token_attended}` : "Doctor pending";
      this.triggerEmergency(
        `${em.token_number} - ${em.token_name || "Emergency patient"}`,
        `${em.service?.name || em.service_name || "Emergency Department"} • ${attended}${room ? ` (Room ${room})` : ""}`
      );
    },
    dismissEmergency() {
      this.activeEmergency = null;
    },
    handleTabChange(tab) {
      this.activeTab = tab;
      if (tab === "dashboard" || !tab) this.loadDoctorSession(true);
    },
    isDoctorUser(item) {
      if (!item) return false;
      if (this.isOtherTenant(item)) return false;
      if (item.is_doctor) return true;
      const u = item.user || {};
      const r = cleanRoleKey(item.role || item.role_name || item.role_slug || roleNameOfUser(u));

      const currentAdminEmail = lower(localStorage.getItem("adminEmail")).trim();
      const currentAdminName = lower(localStorage.getItem("userName")).trim();
      if (currentAdminEmail && item.email && lower(item.email) === currentAdminEmail) return false;

      const rawName = String(item.name || item.attendant_name || u.name || "").trim();
      if (currentAdminName && lower(rawName) === currentAdminName) return false;

      if (hasAny(r, NON_DOCTOR_ROLE_KEYWORDS.filter((k) => k !== "reception" && k !== "nurse")) || (r.includes("admin") && !r.includes("doctor"))) return false;
      if (hasAny(r, DOCTOR_KEYWORDS)) return true;

      const lowerName = lower(rawName);
      return lowerName.startsWith("dr.") || lowerName.startsWith("dr ") || lowerName.startsWith("doctor ");
    },

    /* ----------------------------- shifts ------------------------------- */
    findSavedShiftForDoctor(id, email, name) {
      const strId = String(id || "").trim();
      const cleanEmail = lower(email).trim();
      const cleanName = stripDr(name);
      const all = readAllShifts();

      try {
        for (const k of [strId, cleanEmail, cleanName]) {
          if (!k) continue;
          const cached = localStorage.getItem(`doctorShift_${k}`);
          if (cached) return JSON.parse(cached);
          if (all[k]) return all[k];
        }

        const found = Object.values(all).find((item) => {
          if (!item) return false;
          if (strId && [item.doctor_id, item.id, item.user_id].some((x) => String(x) === strId)) return true;
          if (cleanEmail && item.doctor_email && lower(item.doctor_email) === cleanEmail) return true;
          if (cleanName && item.doctor_name) {
            const c = stripDr(item.doctor_name);
            if (c.includes(cleanName) || cleanName.includes(c)) return true;
          }
          return false;
        });
        if (found) return found;

        const sessionStr = localStorage.getItem("doctorShiftSession");
        if (sessionStr) {
          const s = JSON.parse(sessionStr);
          if (
            (s.doctor_email && cleanEmail && lower(s.doctor_email) === cleanEmail) ||
            (s.doctor_id && strId && String(s.doctor_id) === strId) ||
            (s.doctor_name && cleanName && stripDr(s.doctor_name).includes(cleanName)) ||
            (this.isDoctor && (strId === "1" || strId === String(this.doctors[0]?.id)))
          ) {
            return s;
          }
        }
      } catch (e) {
        console.warn("Could not find saved shift:", e.message);
      }
      return null;
    },
    saveShiftRecord(keys, data) {
      const all = readAllShifts();
      keys.filter(Boolean).forEach((k) => {
        localStorage.setItem(`doctorShift_${k}`, JSON.stringify(data));
        all[k] = data;
      });
      localStorage.setItem("hospitalDoctorShifts", JSON.stringify(all));
    },
    getDoctorShift(doc) {
      if (!doc) return "09:00 - 17:00";
      if (doc.start_time && doc.end_time) return `${doc.start_time} - ${doc.end_time}`;
      if (doc.shift && doc.shift !== "09:00 - 17:00") return doc.shift;
      const saved = this.findSavedShiftForDoctor(doc.user_id || doc.id, doc.email, doc.doctor_name || doc.name);
      if (saved && (saved.start_time || saved.shift)) return shiftLabel(saved);
      if (this.isDoctor && (doc.is_current || doc.id === this.doctors[0]?.id) && this.activeDoctorSession) {
        return shiftLabel(this.activeDoctorSession);
      }
      return doc.shift || "09:00 - 17:00";
    },
    getDoctorSlot(doc) {
      if (!doc) return 30;
      if (doc.slot_duration_minutes) return Number(doc.slot_duration_minutes);
      const saved = this.findSavedShiftForDoctor(doc.user_id || doc.id, doc.email, doc.doctor_name || doc.name);
      if (saved?.slot_duration_minutes) return Number(saved.slot_duration_minutes);
      if (this.isDoctor && (doc.is_current || doc.id === this.doctors[0]?.id) && this.activeDoctorSession?.slot_duration_minutes) {
        return Number(this.activeDoctorSession.slot_duration_minutes);
      }
      return 30;
    },
    onSessionStarted() {
      this.loadDoctorSession(true);
    },

    /* -------------------------- status syncing -------------------------- */
    doctorMatches(doc, { dId, dEmail, dName }) {
      const docId = String(doc.id || doc.user_id || "").trim();
      const docEmail = lower(doc.email).trim();
      const docCleanName = stripDr(doc.doctor_name || doc.name);
      return (
        (dId && docId && dId === docId) ||
        (dEmail && docEmail && dEmail === docEmail) ||
        (dName && docCleanName && (docCleanName === dName || docCleanName.startsWith(dName) || dName.startsWith(docCleanName)))
      );
    },
    /** Single place that applies an incoming status change to the table and session banner. */
    syncDoctorStatus(detail, persistSelf = false) {
      const newStatus = detail.status || detail.availability_status;
      if (!newStatus) return;

      const ids = {
        dId: String(detail.doctor_id || detail.user_id || "").trim(),
        dEmail: lower(detail.email || detail.doctor_email).trim(),
        dName: stripDr(detail.doctor_name || detail.name),
      };
      const tokenNum = detail.current_token || detail.currentToken;
      const effectiveToken = lower(newStatus) === "busy" ? tokenNum || "--" : "--";

      if (this.doctors.length > 0) {
        if (ids.dId || ids.dEmail || ids.dName) {
          this.doctors.forEach((doc) => {
            if (!this.doctorMatches(doc, ids)) return;
            this.setDocStatus(doc, newStatus);
            if (effectiveToken !== "--") doc.currentToken = effectiveToken;
          });
        } else if (this.isDoctor && this.doctors.length === 1) {
          this.setDocStatus(this.doctors[0], newStatus);
        }

        if (persistSelf && this.isDoctor) {
          const currentUserId = String(localStorage.getItem("id") || "").trim();
          const currentUserName = stripDr(getStoredName());
          const isMe =
            (currentUserId && ids.dId === currentUserId) ||
            (ids.dName && (currentUserName === ids.dName || currentUserName.includes(ids.dName)));
          if (isMe) {
            this.setDocStatus(this.doctors[0], newStatus);
            if (effectiveToken !== "--") {
              this.doctors[0].currentToken = effectiveToken;
              localStorage.setItem("current_token", effectiveToken);
            }
            writeLocalStatus(newStatus);
          }
        }
      }

      if (this.activeDoctorSession) {
        const sessDocId = String(this.activeDoctorSession.doctor_id || this.activeDoctorSession.id || "").trim();
        if (!ids.dId || sessDocId === ids.dId) {
          this.activeDoctorSession.status = newStatus;
          this.activeDoctorSession.availability_status = newStatus;
        }
      }
    },
    handleDoctorStatusSynced(e) {
      if (e && e.detail) this.syncDoctorStatus(e.detail, true);
      this.fetchEmergencyTokens();
    },
    handleDoctorAvailabilityEvent(e) {
      if (e && e.detail) {
        this.syncDoctorStatus(e.detail, false);
        if (e.detail.is_shift_update || e.detail.from_shift_modal) this.onSessionStarted(e.detail);
      }
      this.fetchEmergencyTokens();
    },
    handleStorageEvent(event) {
      if (!event) return;
      if (event.key === "tokens_last_updated" || event.key === "tokensUpdated") {
        this.loadDoctorSession(true);
        this.fetchEmergencyTokens();
      }
      if (event.key === "hospitalDoctorShifts" || event.key === "doctorAvailabilityLastUpdated") {
        this.loadDoctorSession(true);
      }
      if (event.key === "doctor_status_synced") {
        try {
          this.handleDoctorStatusSynced({ detail: JSON.parse(event.newValue) });
        } catch (e) {
          console.debug("Failed to parse doctor_status_synced storage event:", e);
        }
      }
    },
    setStatus(item, newStatus) {
      if (item.status_saving || this.isStatusMatch(item, newStatus)) return;
      this.updateStatus(item, newStatus);
    },
    async updateStatus(item, newStatus) {
      this.setDocStatus(item, newStatus);
      if (lower(newStatus) !== "busy") item.currentToken = "--";
      this.markSaving(item, true);

      const userId = item.user_id || item.id || (item.user ? item.user.id : null);
      const counterId = item.counter_id || null;
      const userEmail = item.email || "";
      const doctorName = item.doctor_name || item.name || item.attendant_name;
      const effectiveToken = lower(newStatus) === "busy" ? item.currentToken || "--" : "--";

      if (this.activeDoctorSession) {
        const sessDocId = String(this.activeDoctorSession.doctor_id || this.activeDoctorSession.id || "").trim();
        if (userId && sessDocId === String(userId)) {
          this.activeDoctorSession.status = newStatus;
          this.activeDoctorSession.availability_status = newStatus;
        }
      }

      const syncDetail = {
        doctor_id: userId,
        user_id: userId,
        email: userEmail,
        doctor_email: userEmail,
        doctor_name: doctorName,
        status: newStatus,
        availability_status: newStatus,
        current_token: effectiveToken,
        currentToken: effectiveToken,
        counter_id: counterId,
      };
      this.broadcastStatus(syncDetail);

      try {
        const token = getAuthToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const base = (process.env.VUE_APP_API_BASE_URL || process.env.VUE_APP_API_URL || "/api").replace(/\/+$/, "");
        const apiUrl = base.endsWith("/api") ? base : `${base}/api`;

        const startTime = item.start_time || "20:00";
        const endTime = item.end_time || "21:00";
        const payload = {
          start_time: startTime,
          end_time: endTime,
          slot_duration_minutes: Number(item.slot_duration_minutes) || 30,
          shift: item.shift || `${startTime} - ${endTime}`,
          availability_status: newStatus,
          status: newStatus,
          current_token: effectiveToken,
          currentToken: effectiveToken,
          counter_id: counterId,
          doctor_id: userId,
          user_id: userId,
          email: userEmail,
          doctor_email: userEmail,
          doctor_name: doctorName,
        };

        // Each endpoint is best-effort; a failure in one should not block the others.
        const attempt = async (label, request) => {
          try { await request(); } catch (err) { console.warn(`${label} skipped:`, err.message); }
        };

        await attempt("set-timing sync", () => axios.post(`${apiUrl}/v1/admin/doctor/set-timing`, payload, { headers }));

        if (userId) {
          await attempt("User update sync", () =>
            axios.post(`${apiUrl}/v1/admin/user/update/${userId}`, {
              availability_status: newStatus,
              doctor_status: newStatus,
              current_token: effectiveToken,
              status: 1,
            }, { headers })
          );
        }

        if (counterId && !this.isDoctor && item.user_id && String(item.user_id) === String(userId)) {
          await attempt("Counter update", () =>
            axios.put(`${apiUrl}/v1/admin/counter/update/${counterId}`, {
              availability_status: newStatus,
              current_token: effectiveToken,
              status: newStatus === "Available" ? 1 : 0,
            }, { headers })
          );
        }

        await attempt("Doctor status endpoint", () => axios.post(`${apiUrl}/v1/admin/doctor/status`, payload, { headers }));

        this.setDocStatus(item, newStatus);

        if (this.socket && this.socket.connected) this.socket.emit("doctor_status_updated", payload);
        window.dispatchEvent(new CustomEvent("doctor-availability-changed", { detail: payload }));

        try {
          const docId = String(userId || "");
          const docEmail = lower(userEmail).trim();
          const docCleanName = stripDr(item.doctor_name || item.name);
          const all = readAllShifts();
          const previous = (docId && all[docId]) || (docEmail && all[docEmail]) || (docCleanName && all[docCleanName]) || {};
          this.saveShiftRecord([docId, docEmail, docCleanName], {
            ...previous,
            doctor_id: userId,
            doctor_name: item.doctor_name || item.name,
            doctor_email: userEmail,
            status: newStatus,
            availability_status: newStatus,
            current_token: effectiveToken,
            currentToken: effectiveToken,
          });
          localStorage.setItem("doctorAvailabilityLastUpdated", String(Date.now()));
          localStorage.setItem("doctor_status_synced", JSON.stringify(syncDetail));
        } catch (storageErr) {
          console.warn("Could not sync local shift storage:", storageErr);
        }

        if (this.$toast && typeof this.$toast.success === "function") {
          this.$toast.success(`${item.doctor_name || item.name}: status updated to ${newStatus}`);
        }
      } catch (err) {
        console.error("Failed to update status:", err);
        if (this.$toast && typeof this.$toast.error === "function") {
          this.$toast.error("Failed to update status in database");
        }
      } finally {
        this.markSaving(item, false);
      }
    },

    /* ---------------------------- state setters --------------------------- */
    // Kept synchronous on purpose: writing component state straight after an
    // `await` trips ESLint's require-atomic-updates rule.
    setFetching(value) {
      this.isFetchingSession = value;
    },
    markSaving(item, value) {
      item.status_saving = value;
    },
    setCounters(counters) {
      this.counters = Array.isArray(counters) ? counters.filter((c) => !this.isOtherTenant(c)) : [];
    },
    setDoctors(doctors) {
      this.doctors = doctors;
    },
    storeRolesCache(roles) {
      if (roles.length > 0) this.cachedRoles = roles;
    },
    storeBranchesCache(branches) {
      if (branches.length > 0) this.cachedBranches = branches;
    },

    /* ---------------------------- data loading --------------------------- */
    applyTokenList(rawList) {
      const filtered = Array.isArray(rawList) ? rawList.filter((t) => !this.isOtherTenant(t)) : [];
      this.todayTokens = filtered;
      this.emergencyTokens = filtered.filter(isEmergencyToken);
    },
    /** One shared in-flight request, so socket bursts do not fire a request per event. */
    loadTodayTokens() {
      if (this.tokensRequest) return this.tokensRequest;
      const token = getAuthToken();
      if (!token) return Promise.resolve(this.todayTokens);

      const base = getApiBase();
      const apiUrl = base.endsWith("/api") ? base : `${base}/api`;
      const url = `${apiUrl}/v1/admin/tokens/list?status=today&all=true`;

      this.tokensRequest = axios
        .get(url, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          const rawList = extractItems(res);
          this.applyTokenList(rawList);
          return this.todayTokens;
        })
        .catch((e) => {
          console.warn("Could not fetch tokens:", e);
          return this.todayTokens;
        })
        .finally(() => {
          this.tokensRequest = null;
        });
      return this.tokensRequest;
    },
    fetchEmergencyTokens() {
      return this.loadTodayTokens();
    },
    async fetchDisplays() {
      try {
        const token = getAuthToken();
        if (!token) return;
        const apiUrl = getApiBase();
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const rawRole = lower(localStorage.getItem("userRole") || localStorage.getItem("role")).trim();
        const userId = localStorage.getItem("id");
        let url = `${apiUrl}/v1/admin/user-device-list`;
        if (!rawRole.includes("superadmin") && !rawRole.includes("reception") && userId) {
          url += `?user_id=${userId}`;
        }

        const [deviceRes, playlistRes] = await Promise.allSettled([
          axios.get(url, config),
          axios.get(`${apiUrl}/v1/admin/playlists`, config),
        ]);

        const rawDevices = deviceRes.status === "fulfilled" ? extractItems(deviceRes.value) : [];
        const playlistMap = {};
        if (playlistRes.status === "fulfilled") {
          extractItems(playlistRes.value).forEach((p) => {
            if (p && p.id) playlistMap[p.id] = p.playlist_name || p.name;
          });
        }

        const filteredDevices = rawDevices.filter((d) => !this.isOtherTenant(d));

        this.displays = filteredDevices.map((d, idx) => {
          const isOnline = lower(d.status) === "online";
          const playlist = d.playlist_master_id && playlistMap[d.playlist_master_id]
            ? playlistMap[d.playlist_master_id]
            : d.playlist_master_id ? `Playlist #${d.playlist_master_id}` : "General OPD Media";

          return {
            id: d.id || idx + 1,
            name: d.device_name || `Display TV #${d.id || idx + 1}`,
            location: d.device_position || (d.branch?.branch_name ? `${d.branch.branch_name}` : "Main OPD Lobby"),
            playlist,
            online: isOnline,
            status: d.status || (isOnline ? "Online" : "Offline"),
            tenant_id: d.tenant_id || d.tenantId,
          };
        });
      } catch (err) {
        console.warn("Could not fetch displays:", err);
      }
    },
    applyDynamicTenant(dynamicName) {
      const realHosp = this.resolvedHospitalName;
      if (realHosp && !lower(realHosp).includes("metrotrust")) {
        this.tenantName = realHosp;
      } else if (dynamicName && dynamicName !== "Master" && !lower(dynamicName).includes("metrotrust")) {
        this.tenantName = dynamicName;
        localStorage.setItem("tenant_name", dynamicName);
        localStorage.setItem("company_name", dynamicName);
      }
    },
    getUserContext() {
      const token = getAuthToken();
      const userName = getStoredName().trim();
      return {
        token,
        apiUrl: getApiBase(),
        config: { headers: { Authorization: `Bearer ${token}` } },
        userId: localStorage.getItem("id"),
        userName,
        userEmail: lower(getStoredEmail()).trim(),
        cleanDocName: stripDr(userName),
      };
    },
    async loadDoctorSession(silent = false) {
      if (this.isFetchingSession) return;
      this.setFetching(true);
      try {
        this.fetchDisplays();
        this.fetchEmergencyTokens();
        const ctx = this.getUserContext();
        this.activeDoctorSession = null;
        if (ctx.token) await this.loadAdminView(silent, ctx);
      } finally {
        this.setFetching(false);
      }
    },

    /* ------------------------------ admin view -------------------------- */
    async loadAdminView(silent, { apiUrl, config, userId: currentUserId, userName: currentUserName, userEmail: currentUserEmail }) {
      try {
        const safeGet = (path) =>
          axios.get(`${apiUrl}/v1/admin/${path}`, config).catch((e) => {
            console.warn(`[MedicalDashboard] Endpoint ${path} not available:`, e.message);
            return { data: [] };
          });
        const userPath = () => `user?_t=${Date.now()}`;
        const rejected = { status: "rejected" };
        let rolesRes = rejected, candidatesRes = rejected, usersRes = rejected, attendantsRes = rejected;
        let countersRes = rejected, branchesRes = rejected, rolesListRes = rejected, servicesRes = rejected;

        const canUseCache = silent && this.cachedRoles?.length > 0 && this.cachedBranches?.length > 0;
        if (canUseCache) {
          [countersRes, usersRes, servicesRes] = await Promise.allSettled([safeGet("counter"), safeGet(userPath()), safeGet("services/list")]);
          if (this.branches.length === 0 && this.cachedBranches?.length > 0) {
            this.branches = this.cachedBranches;
          }
        } else {
          [rolesRes, candidatesRes, usersRes, attendantsRes, countersRes, branchesRes, rolesListRes, servicesRes] = await Promise.allSettled([
            safeGet("roles"), safeGet("candidate"), safeGet(userPath()), safeGet("user/attendants"), safeGet("counter"), safeGet("branches"), safeGet("roles/list"), safeGet("services/list"),
          ]);
        }

        const todayList = await this.loadTodayTokens();
        const activeServingTokens = (todayList || []).filter((t) => SERVING_STATUSES.includes(lower(t.status)));

        // ---- roles (merge API, cache, localStorage; de-duplicate by id)
        let rawRoles = [...extractItems(rolesRes), ...extractItems(rolesListRes)];
        if (rawRoles.length === 0 && this.cachedRoles?.length > 0) {
          rawRoles = [...this.cachedRoles];
        } else {
          try {
            const stored = JSON.parse(localStorage.getItem("roles") || "[]");
            if (Array.isArray(stored)) rawRoles.push(...stored);
          } catch (e) {
            console.warn("Could not read cached roles:", e);
          }
        }
        const roles = [];
        const seenRoleIds = new Set();
        rawRoles.forEach((r) => {
          if (r && (r.id !== undefined || r.role_id !== undefined)) {
            const id = String(r.id !== undefined ? r.id : r.role_id);
            if (!seenRoleIds.has(id)) {
              seenRoleIds.add(id);
              roles.push(r);
            }
          }
        });
        this.storeRolesCache(roles);

        const otherTenant = (rec) => this.isOtherTenant(rec);

        const roleKeyOf = (r) => cleanRoleKey(r.name || r.role_name || r.slug || r.title);
        const doctorRoleIds = new Set();
        roles.forEach((r) => {
          const key = roleKeyOf(r);
          if (hasAny(key, ["token", "admin", "reception", "operator", "teller", "supervisor"])) return;
          if (hasAny(key, DOCTOR_KEYWORDS)) doctorRoleIds.add(String(r.id !== undefined ? r.id : r.role_id));
        });
        const findRole = (roleId) => roles.find((r) => String(r.id) === String(roleId) || String(r.role_id) === String(roleId));

        // ---- raw collections
        const rawCounters = countersRes.status === "fulfilled" ? extractItems(countersRes.value) : [];
        const counters = rawCounters.filter((c) => !this.isOtherTenant(c));
        this.setCounters(counters);

        let branches = branchesRes.status === "fulfilled" ? extractItems(branchesRes.value) : [];
        if (branches.length === 0 && this.cachedBranches?.length > 0) branches = this.cachedBranches;
        else this.storeBranchesCache(branches);
        this.branches = branches.filter((br) => !this.isOtherTenant(br));

        let services = servicesRes && servicesRes.status === "fulfilled" ? extractItems(servicesRes.value) : [];
        this.services = services.filter((s) => !this.isOtherTenant(s));

        const branchNameById = (id) => {
          const b = this.branches.find((br) => String(br.id) === String(id));
          return b ? b.branch_name || b.name || "" : "";
        };

        const rawCandidates = candidatesRes.status === "fulfilled" ? extractItems(candidatesRes.value) : [];
        const rawUsers = usersRes.status === "fulfilled" ? extractItems(usersRes.value) : [];
        const rawAttendants = attendantsRes.status === "fulfilled" ? extractItems(attendantsRes.value) : [];

        // ---- merge candidates / users / attendants into one list
        const allUsersMap = new Map();
        [...rawCandidates, ...rawUsers, ...rawAttendants].forEach((u) => {
          if (!u || !(u.id || u.email || u.name || u.f_name)) return;
          if (otherTenant(u)) return;
          const key = u.id ? `id_${u.id}` : u.email ? `email_${lower(u.email)}` : `name_${lower(u.name || u.f_name)}`;
          const prev = allUsersMap.get(key);
          if (!prev) {
            allUsersMap.set(key, { ...u });
          } else {
            allUsersMap.set(key, {
              ...u,
              ...prev,
              role_name: prev.role_name || u.role_name,
              role_id: prev.role_id || u.role_id,
              role: prev.role || u.role,
              department: prev.department || u.department || u.branch_name,
              speciality: prev.speciality || u.speciality,
            });
          }
        });
        const allUsers = Array.from(allUsersMap.values());

        const isDoctorCandidate = (u) => {
          if (!u) return false;
          const uId = String(u.id || "");
          const uEmail = lower(u.email).trim();
          const uName = lower(u.name || `${u.f_name || ""} ${u.l_name || ""}`).trim();

          if (otherTenant(u)) return false;
          if (!this.isDoctor) {
            if (currentUserId && uId === String(currentUserId)) return false;
            if (currentUserEmail && uEmail === currentUserEmail) return false;
            if (currentUserName && uName === lower(currentUserName)) return false;
          }

          let rName = "";
          if (u.role_id && roles.length > 0) {
            const matchedRole = findRole(u.role_id);
            if (matchedRole) rName = lower(matchedRole.name || matchedRole.role_name || matchedRole.title);
          }
          if (!rName) {
            rName = lower(u.role_name || u.role_slug || u.roleName || u.user_role || (u.role && (u.role.name || u.role.slug || u.role.role_name)) || u.role);
          }
          const cleanR = cleanRoleKey(rName);

          if (cleanR.includes("admin") && !cleanR.includes("doctor")) return false;
          if (u.role_id && doctorRoleIds.has(String(u.role_id))) return true;
          if (hasAny(cleanR, DOCTOR_KEYWORDS)) return true;
          if (uName.startsWith("dr.") || uName.startsWith("dr ") || uName.startsWith("doctor ")) return true;
          if (Number(u.type) === 2 || String(u.installation_type_name || "").toLowerCase().includes("medical")) return true;

          return counters.some((c) => (c.user_id && String(c.user_id) === uId) || (u.counter_id && String(c.id) === String(u.counter_id)));
        };

        const withDefaults = (start, end, slot, shift) => {
          const s = start || "09:00";
          const e = end || "17:00";
          return { start: s, end: e, slot: slot || 30, shift: shift || `${s} - ${e}` };
        };
        const toRecord = (fields) => ({
          ...fields,
          doctor_name: fields.name,
          statusBadgeClass: this.getStatusBadgeClass(fields.status),
          availability_status: fields.status,
          is_current: false,
          role: "Doctor",
          role_name: "Doctor",
          is_doctor: true,
          tenant_id: fields.tenant_id || this.currentTenantId,
        });

        const buildRecord = (cand, effectiveCounter, idx, isAttendantOnly = false) => {
          const rawName = String(cand.name || cand.attendant_name || `${cand.f_name || ""} ${cand.l_name || ""}`.trim() || `Doctor ${idx + 1}`);
          const cleanName = stripDr(rawName);
          const lowerClean = cleanName.toLowerCase();

          const servingToken = findServingToken(activeServingTokens, cand.id || cand.user_id, effectiveCounter?.id, cleanName);

          let deptName = cand.speciality || cand.department || cand.department_name || "";
          if (!deptName && cand.branch_id) deptName = branchNameById(cand.branch_id);
          if (!deptName && effectiveCounter?.branch?.branch_name) deptName = effectiveCounter.branch.branch_name;
          if (!deptName && effectiveCounter?.branch_id) deptName = branchNameById(effectiveCounter.branch_id);
          if (!deptName && effectiveCounter?.speciality) deptName = effectiveCounter.speciality;
          if (!deptName) {
            const b = branches.find((br) => (br.user_id && String(br.user_id) === String(cand.id)) || (br.contact_person && lower(br.contact_person).includes(lowerClean)));
            if (b) deptName = b.branch_name || b.name || "";
          }
          if (!deptName) deptName = isAttendantOnly ? "General Medicine" : "General OPD";

          let roomNo = cand.room || cand.counter_name || "";
          if (!roomNo && effectiveCounter?.name) {
            const digits = effectiveCounter.name.replace(/[^0-9]/g, "");
            roomNo = digits ? digits.padStart(2, "0") : effectiveCounter.name;
          }
          if (!roomNo) roomNo = "01";

          const isOnline = (effectiveCounter && (effectiveCounter.status === "Active" || effectiveCounter.status === 1)) || cand.status == 1;
          const isDedicated = effectiveCounter && effectiveCounter.user_id && String(effectiveCounter.user_id) === String(cand.id);

          const docId = cand.id || cand.user_id || (effectiveCounter ? effectiveCounter.id : 100 + idx);
          const p = this.findSavedShiftForDoctor(docId, cand.email || cand.user?.email, rawName);

          let docStart = p?.start_time || cand.start_time || (isDedicated ? effectiveCounter.start_time : null);
          let docEnd = p?.end_time || cand.end_time || (isDedicated ? effectiveCounter.end_time : null);
          let docSlot = Number(p?.slot_duration_minutes || cand.slot_duration_minutes || (isDedicated ? effectiveCounter.slot_duration_minutes : null)) || null;
          let docShift = docStart && docEnd ? `${docStart} - ${docEnd}` : p?.shift || cand.shift || null;

          if (!docStart && docShift && docShift.includes("-")) {
            const parts = docShift.split("-").map((s) => s.trim());
            docStart = parts[0];
            docEnd = parts[1];
          }
          const sh = withDefaults(docStart, docEnd, docSlot, docShift);

          let docToken = "--";
          let docStatus = "Available";
          if (servingToken) {
            docToken = servingToken.token_number;
            docStatus = "Busy";
          } else {
            if (effectiveCounter?.current_token && effectiveCounter.current_token !== "--" && (!effectiveCounter.user_id || String(effectiveCounter.user_id) === String(cand.id))) {
              docToken = effectiveCounter.current_token;
            }
            const backendStatus = cand.availability_status || cand.doctor_status || p?.status || p?.availability_status || (isDedicated ? effectiveCounter.availability_status : null);
            docStatus = backendStatus || (isOnline ? "Available" : "On Break");
            if (docStatus === "Busy") docStatus = "Available";
          }

          if (p?.room && (!cand.room || isAttendantOnly)) roomNo = p.room;
          if (p?.department && (!cand.department || isAttendantOnly)) deptName = p.department;

          const formattedName = formatDoctorName(rawName);
          return toRecord({
            id: docId,
            counter_id: effectiveCounter ? effectiveCounter.id : cand.counter_id || null,
            counter_name: effectiveCounter ? effectiveCounter.name : cand.counter_name || null,
            user_id: cand.user_id || cand.id || (effectiveCounter ? effectiveCounter.user_id : null),
            name: formattedName,
            doctor_name: formattedName,
            attendant_name: cand.attendant_name || rawName,
            initials: getInitials(cleanName),
            email: cand.email || cand.user?.email || `doctor${docId}@hospital.com`,
            speciality: deptName,
            department: deptName,
            room: roomNo,
            currentToken: docToken,
            shift: sh.shift,
            start_time: sh.start,
            end_time: sh.end,
            slot_duration_minutes: sh.slot,
            status: docStatus,
            tenant_id: cand.tenant_id || cand.tenantId || effectiveCounter?.tenant_id || this.currentTenantId || null,
            branch_id: cand.branch_id || cand.dept_id || effectiveCounter?.branch_id || null,
            dept_id: cand.dept_id || cand.branch_id || effectiveCounter?.branch_id || null,
            raw: cand,
          });
        };

        // ---- doctors that come from user records
        const doctorList = allUsers.filter(isDoctorCandidate).map((cand, idx) => {
          const cleanName = stripDr(cand.name || `${cand.f_name || ""} ${cand.l_name || ""}`);
          const lowerClean = cleanName.toLowerCase();
          const effectiveCounter =
            counters.find((c) =>
              (c.user_id && String(c.user_id) === String(cand.id)) ||
              (cand.counter_id && String(c.id) === String(cand.counter_id)) ||
              (c.user && String(c.user.id) === String(cand.id)) ||
              (c.email && cand.email && lower(c.email) === lower(cand.email)) ||
              (c.attendant_name && cleanName && (stripDr(c.attendant_name).includes(lowerClean) || lowerClean.includes(stripDr(c.attendant_name))))
            ) || null;

          return buildRecord(cand, effectiveCounter, idx, false);
        });

        // ---- doctors that exist as counter attendants
        counters.forEach((c, cIdx) => {
          if (otherTenant(c)) return;
          const attName = String(c.attendant_name || c.user?.name || "").trim();
          if (!attName) return;

          const lowerAtt = stripDr(attName).toLowerCase();
          const alreadyIncluded = doctorList.some(
            (d) =>
              (c.user_id && String(d.id) === String(c.user_id)) ||
              (c.user_id && String(d.user_id) === String(c.user_id)) ||
              (d.counter_id && String(d.counter_id) === String(c.id)) ||
              lower(d.name).includes(lowerAtt) ||
              lowerAtt.includes(stripDr(d.name))
          );
          if (alreadyIncluded) return;

          let rName = lower(c.user?.role_name || c.user?.role_slug || (c.user?.role && (c.user.role.name || c.user.role.slug)) || c.user?.role || c.role);
          if (!rName && c.user?.role_id) {
            const matchedRole = findRole(c.user.role_id);
            if (matchedRole) rName = lower(matchedRole.name);
          }
          const cleanR = cleanRoleKey(rName);

          const linkedUser = allUsers.find((u) => c.user_id && String(u.id) === String(c.user_id));
          const lowerAttFull = lower(attName);
          const isDoc =
            (linkedUser && isDoctorCandidate(linkedUser)) ||
            hasAny(cleanR, ["doctor", "physician", "consultant", "specialist"]) ||
            (c.user?.role_id && doctorRoleIds.has(String(c.user.role_id))) ||
            lowerAttFull.startsWith("dr.") || lowerAttFull.startsWith("dr ") || lowerAttFull.startsWith("doctor ") ||
            Boolean(attName);
          if (!isDoc) return;

          if (!this.isDoctor) {
            if (currentUserEmail && c.user?.email && lower(c.user.email) === currentUserEmail) return;
            if (currentUserName && lowerAttFull === lower(currentUserName)) return;
          }

          doctorList.push(buildRecord(c.user || { ...c, name: attName }, c, cIdx, true));
        });

        this.setDoctors(doctorList);
      } catch (e) {
        console.error("Error loading doctors for tenant admin:", e);
      }
    },
  },
};
</script>

<style scoped>
/* ---------- Tokens ---------- */
.medical-dashboard {
  --md-bg: #f4f7fb;
  --md-surface: #ffffff;
  --md-line: #e8eef5;
  --md-teal: #0d9488;
  --md-text: #1e293b;
  --md-muted: #64748b;
  background: var(--md-bg);
  border-radius: 20px;
  min-height: 100%;
}

/* ---------- Colour helpers ---------- */
.text-teal { color: #0d9488 !important; }
.bg-teal { background-color: #0d9488 !important; }
.btn-teal { background-color: #0d9488 !important; border-color: #0d9488 !important; }
.bg-teal-soft { background-color: rgba(13, 148, 136, 0.12); }
.btn-outline-teal { border: 1px solid #0d9488 !important; color: #0d9488 !important; background: transparent; }
.btn-outline-teal:hover { background-color: #0d9488 !important; color: #ffffff !important; }
.bg-primary-soft { background-color: rgba(59, 130, 246, 0.12); }
.bg-warning-soft { background-color: rgba(245, 158, 11, 0.12); }
.bg-danger-soft { background-color: rgba(239, 68, 68, 0.12); }
.bg-success-soft { background-color: rgba(34, 197, 94, 0.12); }
.bg-secondary-soft { background-color: rgba(100, 116, 139, 0.15); }
.cursor-pointer { cursor: pointer; }
.min-w-0 { min-width: 0; }

/* ---------- Cards ---------- */
.md-card {
  background: var(--md-surface);
  border-radius: 16px;
  border: 1px solid var(--md-line);
  box-shadow: 0 2px 10px rgba(30, 41, 59, 0.04);
}

/* ---------- KPI ---------- */
.kpi-icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.tone-teal { background: #d9f5f1; color: #0d9488; }
.tone-blue { background: #dbeafe; color: #2563eb; }
.tone-amber { background: #fff1d6; color: #d97706; }
.tone-pink { background: #fde2ec; color: #db2777; }
.kpi-label { font-size: 0.8rem; color: var(--md-muted); }
.kpi-value { font-weight: 800; color: var(--md-text); line-height: 1.1; }
.kpi-total { font-size: 0.9rem; font-weight: 500; color: var(--md-muted); }
.kpi-sub { font-size: 0.75rem; font-weight: 600; }
.text-pink { color: #db2777 !important; }

/* ---------- Tables ---------- */
.md-table thead th {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--md-muted);
  background: #f8fafc;
  border-bottom: 1px solid var(--md-line);
  padding: 0.65rem 0.75rem;
  white-space: nowrap;
}
.md-table tbody td {
  padding: 0.7rem 0.75rem;
  border-bottom: 1px solid #f1f5f9;
}
.md-table tbody tr:last-child td { border-bottom: none; }
.doctor-filter { max-width: 260px; font-size: 0.85rem; }

.room-chip {
  display: inline-block;
  padding: 0.3rem 0.8rem;
  border-radius: 999px;
  background: #f1f5f9;
  color: var(--md-text);
  font-size: 0.8rem;
  font-weight: 700;
}
.token-chip {
  display: inline-block;
  min-width: 52px;
  padding: 0.3rem 0.7rem;
  border-radius: 10px;
  background: #d9f5f1;
  color: #0f766e;
  font-weight: 700;
  font-size: 0.85rem;
  text-align: center;
  font-variant-numeric: tabular-nums;
}
.token-chip-empty { background: #f1f5f9; color: #94a3b8; }

/* ---------- Status control ---------- */
.status-cell { min-width: 230px; }
.status-pills {
  display: inline-flex;
  gap: 4px;
  padding: 3px;
  border-radius: 999px;
  background: #f1f5f9;
}
.status-pill {
  border: none;
  background: transparent;
  color: var(--md-muted);
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.3rem 0.65rem;
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.status-pill:hover:not(:disabled):not(.active) { background: #e2e8f0; }
.status-pill:focus-visible { outline: 2px solid #0d9488; outline-offset: 1px; }
.status-pill:disabled { opacity: 0.55; cursor: wait; }
.status-pill.active { color: #ffffff; }
.status-pill.pill-available.active { background: #10b981; }
.status-pill.pill-busy.active { background: #ef4444; }
.status-pill.pill-break.active { background: #f59e0b; }
.status-pill.pill-offline.active { background: #64748b; }

.status-dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; }
.status-dot.dot-available { background-color: #10b981; box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.25); }
.status-dot.dot-busy { background-color: #ef4444; box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.25); }
.status-dot.dot-break { background-color: #f59e0b; box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.25); }
.status-dot.dot-offline { background-color: #94a3b8; box-shadow: 0 0 0 2px rgba(148, 163, 184, 0.25); }

/* ---------- Departments Section (Image 2) ---------- */
.department-card {
  position: relative;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 1rem 1.25rem;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease, background-color 0.15s ease;
  overflow: hidden;
  cursor: pointer;
}

.department-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
}

.department-card.department-card-active {
  border: 2px solid #0d9488 !important;
  box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.15), 0 4px 14px rgba(13, 148, 136, 0.1) !important;
  background-color: #fbfdfd !important;
}

.department-accent {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 5px;
  border-top-left-radius: 14px;
  border-bottom-left-radius: 14px;
}

.accent-teal { background-color: #0d9488; }
.accent-amber { background-color: #d97706; }
.accent-red { background-color: #dc2626; }

.dept-progress-track {
  width: 100%;
  height: 6px;
  background-color: #f1f5f9;
  border-radius: 999px;
  overflow: hidden;
}

.dept-progress-bar {
  height: 100%;
  border-radius: 999px;
  transition: width 0.3s ease;
}

.bar-teal { background-color: #0d9488; }
.bar-amber { background-color: #d97706; }
.bar-red { background-color: #dc2626; }

.btn-outline-teal {
  color: #0d9488;
  border-color: #0d9488;
  background-color: transparent;
}

.btn-outline-teal:hover {
  color: #ffffff;
  background-color: #0d9488;
  border-color: #0d9488;
}

.emergency-list { max-height: 290px; overflow-y: auto; }
.emergency-btn { border-radius: 10px; }

.screen-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 0.75rem;
  border-radius: 12px;
  border: 1px solid var(--md-line);
}
.screen-status-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.hover-bg-light:hover { background-color: #f1f5f9; }

/* Pulse only when the person has not asked for reduced motion */
@media (prefers-reduced-motion: no-preference) {
  .animate-pulse { animation: pulse-border 2s infinite; }
}
@keyframes pulse-border {
  0% { opacity: 1; }
  50% { opacity: 0.85; }
  100% { opacity: 1; }
}

/* ---------- Counter drilldown ---------- */
.border-start-lg { border-left: 1px solid #f1f5f9; }
@media (max-width: 991.98px) {
  .border-start-lg { border-left: none; border-top: 1px solid #f1f5f9; padding-top: 1.5rem; }
}
.donut-chart-container { width: 210px; height: 210px; }
.donut-svg { width: 100%; height: 100%; transform: rotate(-90deg); border-radius: 50%; }
.donut-segment { transition: stroke-width 0.2s ease, opacity 0.2s ease; }
.donut-segment:hover { stroke-width: 18; opacity: 0.9; }
.donut-center-label {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
}
.legend-color-dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; flex-shrink: 0; }

/* ========================================================
   ADMIN DASHBOARD STYLES (MATCHING USER MOCKUPS)
   ======================================================== */
.dept-accent-amber {
  border-left: 4px solid #d97706 !important;
}
.dept-accent-teal {
  border-left: 4px solid #0d9488 !important;
}
.dept-accent-red {
  border-left: 4px solid #dc2626 !important;
}

.dept-title {
  font-size: 0.96rem;
  font-weight: 700;
  color: #1e293b;
}

.dept-subtitle {
  font-size: 0.8rem;
  color: #64748b;
}

.bg-amber {
  background-color: #d97706 !important;
}
.bg-red {
  background-color: #dc2626 !important;
}

/* Doctors Filter Pills */
.doctor-filter-pills {
  background: #f1f5f9;
  border-radius: 9999px;
  padding: 3px;
  gap: 2px;
}

.btn-doctor-pill {
  border: none;
  background: transparent;
  color: #475569;
  font-size: 0.82rem;
  font-weight: 500;
  padding: 5px 14px;
  border-radius: 9999px;
  transition: all 0.2s ease;
  cursor: pointer;
}
.btn-doctor-pill:hover {
  color: #0f172a;
}
.btn-doctor-pill.active {
  background-color: #0d9488;
  color: #ffffff;
  font-weight: 600;
  box-shadow: 0 2px 6px rgba(13, 148, 136, 0.25);
}

/* Doctor Status Badges */
.badge-status-pill {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 9999px;
  font-size: 0.78rem;
  font-weight: 600;
  text-align: center;
}
.badge-status-available {
  background: #dcfce7;
  color: #16a34a;
}
.badge-status-busy {
  background: #fef3c7;
  color: #d97706;
}
.badge-status-break {
  background: #e0e7ff;
  color: #4338ca;
}
.badge-status-offline {
  background: #f1f5f9;
  color: #64748b;
}

.badge-no-room {
  display: inline-block;
  background: #fee2e2;
  color: #dc2626;
  padding: 3px 10px;
  border-radius: 9999px;
  font-size: 0.76rem;
  font-weight: 600;
}

/* Devices Section */
.badge-device-online-count {
  background: #dcfce7;
  color: #16a34a;
  padding: 4px 12px;
  border-radius: 9999px;
  font-size: 0.8rem;
  font-weight: 600;
}

.badge-device-status {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}
.badge-device-offline {
  background: #fee2e2;
  color: #dc2626;
}
.badge-device-unassigned {
  background: #fef3c7;
  color: #d97706;
}
.badge-device-online {
  background: #dcfce7;
  color: #16a34a;
}

.border-subtle {
  border: 1px solid #f1f5f9;
}
.border-bottom-subtle {
  border-bottom: 1px solid #f8fafc;
}

/* Booked and Walk-in Card Styles matching mockup */
.booked-walkin-card {
  border-radius: 16px !important;
}

.booked-walkin-progress {
  display: flex;
  width: 100%;
  height: 10px;
  background-color: #f1f5f9;
  border-radius: 9999px;
  overflow: hidden;
}

.progress-seg-booked {
  background-color: #2563eb;
  height: 100%;
  transition: width 0.3s ease;
}

.progress-seg-walkin {
  background-color: #0d9488;
  height: 100%;
  transition: width 0.3s ease;
}

.booked-walkin-row {
  border-bottom: 1px solid #f1f5f9;
}

.booking-type-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}

.booking-type-progress-seg {
  height: 100%;
  transition: width 0.3s ease;
}

/* ========================================================================== */
/* Token Flow Today Card & Hourly Bar Chart (matching screenshot)             */
/* ========================================================================== */
.token-flow-card {
  border-radius: 16px !important;
}

.token-flow-timing-select {
  max-width: 110px;
  font-size: 0.75rem;
  padding: 3px 20px 3px 8px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  color: #334155;
  background-color: #f8fafc;
  font-weight: 600;
  cursor: pointer;
}
.token-flow-timing-select:focus {
  border-color: #0d9488;
  box-shadow: 0 0 0 2px rgba(13, 148, 136, 0.15);
}

.token-flow-date-picker {
  max-width: 130px;
  font-size: 0.75rem;
  padding: 3px 8px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  color: #334155;
  background-color: #f8fafc;
}
.token-flow-date-picker:focus {
  border-color: #0d9488;
  box-shadow: 0 0 0 2px rgba(13, 148, 136, 0.15);
}

.token-flow-select {
  font-size: 0.75rem;
  padding: 4px 8px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  color: #1e293b;
  background-color: #f8fafc;
  font-weight: 500;
}
.token-flow-select:focus {
  border-color: #0d9488;
  box-shadow: 0 0 0 2px rgba(13, 148, 136, 0.15);
}

/* 4 Status summary pills */
.token-flow-pills-row {
  display: flex;
  gap: 8px;
}

.token-flow-pill {
  background-color: #f1f5f9;
  border-radius: 12px;
  padding: 10px 6px;
  text-align: center;
  border: 1px solid rgba(226, 232, 240, 0.8);
  transition: all 0.2s ease;
}
.token-flow-pill:hover {
  background-color: #e2e8f0;
  transform: translateY(-1px);
}

.token-flow-pill-val {
  font-size: 1.28rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.1;
  margin-bottom: 2px;
}

.token-flow-pill-lbl {
  font-size: 0.72rem;
  font-weight: 500;
  color: #64748b;
  text-transform: capitalize;
  letter-spacing: -0.01em;
}

/* Hourly bar chart */
.token-flow-chart-wrap {
  padding: 8px 4px 4px 4px;
}

.token-flow-bars {
  height: 125px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
}

.token-flow-bar-col {
  flex: 1;
  max-width: 28px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  height: 100%;
  cursor: pointer;
  border-radius: 6px;
  padding: 2px;
  transition: all 0.2s ease;
}
.token-flow-bar-col:hover {
  background-color: rgba(13, 148, 136, 0.08);
}
.token-flow-bar-col:hover .token-flow-bar {
  background-color: #3b82f6 !important;
}

.token-flow-bar-col.bar-col-selected {
  background-color: rgba(37, 99, 235, 0.12) !important;
}
.token-flow-bar-col.bar-col-selected .token-flow-bar {
  background-color: #2563eb !important;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.4);
}
.token-flow-bar-col.bar-col-selected .token-flow-bar-val,
.token-flow-bar-col.bar-col-selected .token-flow-bar-hour {
  color: #1d4ed8 !important;
  font-weight: 800;
}

.token-flow-bar-val {
  font-size: 0.7rem;
  font-weight: 600;
  color: #64748b;
  line-height: 1;
  min-height: 14px;
  display: inline-block;
  transition: color 0.2s ease;
}

.token-flow-bar-track {
  height: 85px;
  width: 100%;
  max-width: 22px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background-color: transparent;
}

.token-flow-bar {
  width: 100%;
  background-color: #84aee4;
  border-radius: 5px 5px 2px 2px;
  transition: height 0.35s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.2s ease;
  min-height: 3px;
}
.token-flow-bar:hover {
  background-color: #5c93d9;
}

.token-flow-bar.bar-peak {
  background-color: #72a2df;
}

.token-flow-bar.bar-active {
  background-color: #2563eb !important;
}

.token-flow-bar-hour {
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  line-height: 1;
  min-height: 14px;
  transition: color 0.2s ease;
}

.token-flow-pill-active {
  border-color: #0d9488 !important;
  box-shadow: 0 0 0 2px rgba(13, 148, 136, 0.25);
  background-color: #f0fdfa !important;
}

.token-flow-footer {
  border-top: 1px solid #f1f5f9;
  padding-top: 8px;
}

.border-start-3 {
  border-left-width: 3px !important;
}

.hourly-patient-drawer {
  animation: slideDownFade 0.2s ease-out;
}
@keyframes slideDownFade {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Department doctors table interaction */
.doctor-row-hover {
  transition: background-color 0.15s ease;
}
.doctor-row-hover:hover {
  background-color: #f8fafc;
}

.table-active-row {
  background-color: #f0fdfa !important;
  border-left: 3px solid #0d9488 !important;
}
</style>